/**
 * The AI Workbench settings declarations, copied from mju-ai-workbench shared/settings/declarations.json
 * (keys without the `mjuWorkbench.` prefix). Generated by scripts/refresh-settings-declarations.mjs: do not
 * edit by hand; refresh from the workbench.
 */
export const SETTINGS_DECLARATIONS_TITLE = "AI Workbench";
/** SHA-256 of the declarations file this copy came from: what a workbench on the same release reports as settingsDigest. */
export const SETTINGS_DECLARATIONS_DIGEST = "c9af812169f2881aa16103efdb402b006c9037b69a0f483764d77e12f9a858ec";
export const SETTINGS_DECLARATIONS = {
    "devcontainer.dockerPath": {
        "type": "string",
        "default": "",
        "description": "Path to the Docker CLI used for dev containers. Leave empty to use 'docker' from PATH; 'podman' works too."
    },
    "devcontainer.cliPath": {
        "type": "string",
        "default": "",
        "description": "Command that runs the Dev Containers CLI (@devcontainers/cli), used to build and start a container. Leave empty to use 'devcontainer' from PATH."
    },
    "devcontainer.useNpx": {
        "type": "boolean",
        "default": false,
        "description": "When the Dev Containers CLI is not installed, fetch and run it with npx. Off by default: it downloads a package from the network each time it is needed."
    },
    "devcontainer.autoDetect": {
        "type": "boolean",
        "default": true,
        "description": "Offer to open a folder in its dev container when it contains a devcontainer.json."
    },
    "targets.ssh": {
        "type": "array",
        "default": [],
        "items": {
            "type": "object",
            "required": [
                "alias"
            ],
            "properties": {
                "alias": {
                    "type": "string",
                    "description": "A Host entry in ~/.ssh/config. The host name, user, port and key come from there; the app never asks for a password."
                },
                "label": {
                    "type": "string",
                    "description": "What the host is called in the app; 'SSH: <alias>' when omitted."
                },
                "tags": {
                    "type": "array",
                    "items": {
                        "type": "string"
                    },
                    "description": "Free-form tags, such as gpu or build."
                }
            }
        },
        "description": "SSH machines this workbench can open repositories on, one per ~/.ssh/config alias. Each needs sshd and a POSIX shell; the app installs what it needs there (and Node, on Linux) over ssh."
    },
    "copilotCliPath": {
        "type": "string",
        "default": "",
        "description": "Path to the GitHub Copilot CLI, for agents on the Copilot runtime. Leave empty to use 'copilot' from PATH (or the COPILOT_CLI_PATH environment variable)."
    },
    "backgroundNotifications": {
        "type": "string",
        "enum": [
            "approvals",
            "all",
            "off"
        ],
        "default": "approvals",
        "description": "Notifications shown while the workbench is not the window in front: 'approvals' = permission requests and errors, 'all' = also agent turn completions, 'off' = none."
    },
    "enableOllamaAgents": {
        "type": "boolean",
        "default": false,
        "description": "Allow spawning agents backed by an Ollama endpoint."
    },
    "enableLmStudioAgents": {
        "type": "boolean",
        "default": false,
        "description": "Allow spawning agents backed by an LM Studio endpoint. LM Studio does not authenticate by default; if you switched that on under Developer → Server Settings, paste one of its tokens into the key field under Settings → Agent backends."
    },
    "enableUnslothAgents": {
        "type": "boolean",
        "default": false,
        "markdownDescription": "Allow spawning agents backed by Unsloth Desktop on this machine. Unlike Ollama it authenticates every request, so it needs an API key: make one in Unsloth under Settings → API and paste it into the key field under **Settings → Agent backends**. Agents on it will not start until it is there."
    },
    "enableLoopBreaker": {
        "type": "boolean",
        "default": true,
        "description": "Cancel an agent's turn when it repeats the same reasoning verbatim without making progress (shows as \"Operation cancelled by user\" followed by a corrective prompt). Disable if local models are being cancelled while working."
    },
    "loopBreaker.repeatThreshold": {
        "type": "number",
        "default": 6,
        "minimum": 3,
        "maximum": 20,
        "description": "How many times the same substantial line must repeat within one turn (with no completed tool call in between) before the loop breaker cancels it. Raise this if agents are being cancelled while legitimately working."
    },
    "turnWatchdog.enabled": {
        "type": "boolean",
        "default": true,
        "description": "Cancel an agent's turn when it has produced no output, tool call or permission request for longer than the idle timeout. Catches agents wedged on a hung model server or a stuck MCP server."
    },
    "turnWatchdog.idleTimeoutMinutes": {
        "type": "number",
        "default": 10,
        "minimum": 2,
        "maximum": 120,
        "description": "Minutes of silence inside a turn before the watchdog cancels it. Local models on slow hardware may need a higher value."
    },
    "turnWatchdog.restartAfterCancel": {
        "type": "boolean",
        "default": true,
        "description": "If the cancel is ignored, kill the agent process and restart it, resuming the same conversation."
    },
    "agents.restart.maxRestarts": {
        "type": "number",
        "default": 3,
        "minimum": 0,
        "maximum": 20,
        "description": "How many times an agent that crashes or stops responding is started again on its conversation within the time window. After that it is left stopped and you get a note in the inbox. 0 never restarts it."
    },
    "agents.restart.backoffSeconds": {
        "type": "number",
        "default": 30,
        "minimum": 0,
        "maximum": 3600,
        "description": "Seconds to wait before the first restart. Each further restart in the same window waits twice as long."
    },
    "agents.restart.windowMinutes": {
        "type": "number",
        "default": 30,
        "minimum": 1,
        "maximum": 1440,
        "description": "The time window restarts are counted in. An agent that has run cleanly for this long gets its full number of restarts back."
    },
    "agents.restart.afterUnansweredNudges": {
        "type": "number",
        "default": 3,
        "minimum": 0,
        "maximum": 20,
        "description": "How many reminders an agent can leave unanswered (status checks, requests to report on its task) before it is treated as stuck and restarted. 0 keeps reminding without restarting."
    },
    "approvals.timeoutMinutes.low": {
        "type": "number",
        "default": 10,
        "minimum": 0,
        "maximum": 10080,
        "description": "Minutes a low-risk request (reading or searching) waits for an answer before it is allowed once automatically. The decision is recorded in the audit log. 0 waits for you however long it takes."
    },
    "approvals.timeoutMinutes.medium": {
        "type": "number",
        "default": 30,
        "minimum": 0,
        "maximum": 10080,
        "description": "Minutes a medium-risk request (editing or moving files) waits for an answer before it is denied automatically, so the agent can carry on another way. The decision is recorded in the audit log. 0 waits for you however long it takes."
    },
    "approvals.timeoutMinutes.high": {
        "type": "number",
        "default": 0,
        "minimum": 0,
        "maximum": 10080,
        "description": "Minutes a high-risk request (running commands or deleting files) waits for an answer before it is denied automatically. It is never allowed without you. 0, the default, waits for you however long it takes."
    },
    "stallNudge.enabled": {
        "type": "boolean",
        "default": true,
        "description": "Automatically prompt an agent to continue when it ends a turn describing what it is about to do without actually calling any tools — a common failure mode for local models."
    },
    "stallNudge.maxNudges": {
        "type": "number",
        "default": 3,
        "minimum": 1,
        "maximum": 10,
        "description": "How many times in a row a stalled agent may be nudged to continue before the workbench gives up and leaves it idle."
    },
    "stallNudge.localOnly": {
        "type": "boolean",
        "default": true,
        "description": "Only nudge stalled agents on a local model (Ollama, LM Studio, the built-in runner). Turn off to nudge agents on every runtime."
    },
    "enableCustomAcpAgents": {
        "type": "boolean",
        "default": false,
        "description": "Offer a custom agent command when starting a solo agent: any program that speaks the Agent Client Protocol (ACP), such as \"agy_acp_server.par --uid=\"."
    },
    "ollamaEndpoint": {
        "type": "string",
        "default": "http://127.0.0.1:11434/v1",
        "description": "Default OpenAI-compatible endpoint for team members using the 'ollama' provider. Can be overridden per member in the team config."
    },
    "lmstudioEndpoint": {
        "type": "string",
        "default": "http://127.0.0.1:1234/v1",
        "description": "Default OpenAI-compatible endpoint for team members using the 'lmstudio' provider. Can be overridden per member in the team config."
    },
    "unslothEndpoint": {
        "type": "string",
        "default": "http://127.0.0.1:8000/v1",
        "description": "Default OpenAI-compatible endpoint for team members using the 'unsloth' provider — the port Unsloth Desktop booted on (8000 by default, 8888 when it was started with -p 8888). Can be overridden per member in the team config."
    },
    "localProxy.enabled": {
        "type": "boolean",
        "default": true,
        "description": "Route Ollama / LM Studio agents through the built-in compatibility proxy. The proxy repairs message and tool-call formats between the agent's runtime and the local server (array content, missing tool-call ids, namespaced/mangled tool names, wrong finish reasons, reasoning fields, empty streams) and records live metrics for the Inference view. Hosted members (Claude Code, Codex, Cline, every runtime that takes a model source, the built-in agent on a stored key) route through it too, so the provider key stays in the proxy rather than the agent's environment. Disable to connect agents directly to the endpoint, which hands them the key."
    },
    "localModel.recoveryAttempts": {
        "type": "number",
        "default": 3,
        "minimum": 0,
        "maximum": 10,
        "description": "How many times the local-model proxy retries a request when the runner itself has fallen over (refused connection, HTML 500) — typically a large model being reloaded into VRAM. Raise it for slow-loading MoE models such as Qwen3; 0 fails the agent's turn on the first refusal."
    },
    "localModel.recoveryMaxWaitSeconds": {
        "type": "number",
        "default": 45,
        "minimum": 5,
        "maximum": 600,
        "description": "Longest the proxy waits for a crashed or reloading local model to come back, per retry. Waits ramp 5s, 20s, then this ceiling, and end early the moment the runner answers a health probe — so a higher value costs nothing when recovery is fast, and buys a slow model the time it actually needs."
    },
    "localModel.streamIdleTimeoutSeconds": {
        "type": "number",
        "default": 180,
        "minimum": 30,
        "maximum": 1800,
        "description": "Silence between streamed chunks that means the local runner died mid-answer. Armed only after the first chunk arrives, so slow prefill is never cut short. Raise it if a model that is genuinely still generating gets cut off."
    },
    "llm.redactSecrets": {
        "type": "boolean",
        "default": true,
        "description": "Redact API keys, tokens and private keys from message content before it leaves the machine for a remote provider. Local providers (Ollama / LM Studio) are never redacted — nothing leaves the machine, and an agent debugging an auth failure needs to see the credential."
    },
    "llm.redactPii": {
        "type": "boolean",
        "default": true,
        "description": "Redact personal data — email addresses, phone numbers, payment card numbers, IBANs, tax file and social security numbers — from message content before it leaves the machine for a hosted provider, alongside the secrets redaction. Pattern-based, so it needs no classifier; the classifier in `.mojoup/policy.json` adds names, addresses and sensitivity on top. Local providers are never redacted."
    },
    "llm.redactPatterns": {
        "type": "array",
        "default": [],
        "items": {
            "type": "object",
            "required": [
                "name",
                "pattern"
            ],
            "properties": {
                "name": {
                    "type": "string",
                    "description": "What the placeholder says: `[redacted:<name>]`."
                },
                "pattern": {
                    "type": "string",
                    "description": "A JavaScript regular expression, applied globally."
                },
                "flags": {
                    "type": "string",
                    "description": "Any of i, m, s, u."
                },
                "enabled": {
                    "type": "boolean",
                    "default": true
                }
            }
        },
        "description": "Your own redaction rules, applied after the secrets and personal-data patterns to everything bound for a hosted provider: customer or ticket ids, internal hostnames, project codenames — anything a regular expression can name. Built and tried out under Settings → Security → Repository policy. A repository may commit its own, so the rules travel with the code."
    },
    "llm.dailyBudget": {
        "type": "number",
        "default": 0,
        "minimum": 0,
        "description": "Daily spend cap across all agents on paid providers, in the currency your provider's list prices are quoted in. Requests are refused with HTTP 403 once the cap is reached. 0 disables the cap. Local providers are always free and never counted."
    },
    "llm.perAgentDailyBudget": {
        "type": "number",
        "default": 0,
        "minimum": 0,
        "description": "Daily spend cap for each individual agent on paid providers. Stops one runaway agent exhausting the shared budget. 0 disables the per-agent cap."
    },
    "llm.requestsPerMinute": {
        "type": "number",
        "default": 0,
        "minimum": 0,
        "description": "Maximum proxied requests per minute per agent. Requests over the limit are refused with HTTP 429 so the caller retries rather than gives up. 0 disables rate limiting."
    },
    "llm.costMap.autoRefresh": {
        "type": "boolean",
        "default": true,
        "description": "Periodically download the LiteLLM model pricing and context-window map so cost estimates stay current. Cached locally and used offline. Turn off to use only the pricing built into the app."
    },
    "llm.costMap.url": {
        "type": "string",
        "default": "https://raw.githubusercontent.com/BerriAI/litellm/main/model_prices_and_context_window.json",
        "description": "Source URL for the model pricing map. Must be HTTPS. Point at a mirror if the default is unreachable from your network."
    },
    "llm.mcpToolProxy.enabled": {
        "type": "boolean",
        "default": false,
        "description": "Execute MCP tool calls inside the proxy instead of passing them back to the agent. Useful for weak local models: the tool round trip never reaches the CLI. Agents on a runtime that runs its own tool servers (Claude Code, Codex, Copilot) are unaffected."
    },
    "llm.mcpToolProxy.servers": {
        "type": "array",
        "default": [],
        "description": "MCP servers the proxy aggregates and exposes to local agents.",
        "items": {
            "type": "object",
            "required": [
                "name",
                "transport"
            ],
            "properties": {
                "name": {
                    "type": "string",
                    "description": "Server name. Prefixed onto tool names when two servers export the same tool."
                },
                "transport": {
                    "type": "string",
                    "enum": [
                        "stdio",
                        "http"
                    ],
                    "description": "How to reach the server."
                },
                "command": {
                    "type": "string",
                    "description": "Executable to spawn (stdio transport)."
                },
                "args": {
                    "type": "array",
                    "items": {
                        "type": "string"
                    },
                    "description": "Arguments for the command (stdio transport)."
                },
                "env": {
                    "type": "object",
                    "additionalProperties": {
                        "type": "string"
                    },
                    "description": "Extra environment variables for the command (stdio transport)."
                },
                "url": {
                    "type": "string",
                    "description": "Server endpoint (http transport)."
                },
                "headers": {
                    "type": "object",
                    "additionalProperties": {
                        "type": "string"
                    },
                    "description": "Extra request headers (http transport)."
                }
            }
        }
    },
    "llm.mcpToolProxy.allowTools": {
        "type": "array",
        "default": [],
        "items": {
            "type": "string"
        },
        "description": "When non-empty, only these tool names are exposed to agents. The way to give a weak local model five tools instead of forty."
    },
    "llm.mcpToolProxy.denyTools": {
        "type": "array",
        "default": [],
        "items": {
            "type": "string"
        },
        "description": "Tool names hidden from agents even when allowed."
    },
    "native.maxToolIterations": {
        "type": "number",
        "default": 24,
        "minimum": 1,
        "maximum": 200,
        "description": "How many tool calls the built-in agent (the native runtime) makes in one turn before it stops and reports. Small local models loop; a larger budget lets a capable model finish a longer task."
    },
    "native.maxConsecutiveToolFailures": {
        "type": "number",
        "default": 3,
        "minimum": 1,
        "maximum": 20,
        "description": "How many malformed calls to the same tool in a row the built-in agent tolerates before it abandons the turn — a model that cannot form valid arguments does not learn to on the twentieth attempt."
    },
    "native.maxToolResultChars": {
        "type": "number",
        "default": 30000,
        "minimum": 1000,
        "maximum": 400000,
        "description": "The longest tool result the built-in agent hands back to the model, in characters; longer ones are cut with a note. Raise it for models with a large context window."
    },
    "localContext.autoCompact": {
        "type": "boolean",
        "default": true,
        "description": "Automatically compact a local agent's conversation (context reset + task resume prompt) when it nears the model runner's loaded context window. Prevents the truncation/empty-stream failures local models hit when the conversation outgrows num_ctx / the loaded context length."
    },
    "localContext.compactThreshold": {
        "type": "number",
        "default": 0.75,
        "minimum": 0.4,
        "maximum": 0.95,
        "description": "Fraction of the local model's context window at which auto-compaction triggers (0.75 = compact when 75% full)."
    },
    "chatApprovals.low": {
        "type": "string",
        "enum": [
            "ask",
            "auto"
        ],
        "default": "ask",
        "description": "Default approval policy for LOW-risk tools (read · search · fetch) of chat/solo agents that are not part of a team. Teams configure their own policy in the team config."
    },
    "chat.gitIsolation": {
        "type": "boolean",
        "default": true,
        "description": "Start chat agents in their own git worktree (their own branch and working copy) instead of the repository's folder. Several can then edit at once safely, and Edit from here can put a turn's files back. Their work is merged into the current branch when chat.mergeWhen says: once the conversation has been idle for a while (the default), when you press Keep it, or after every turn. This is the default for an agent that is not a specialist; a conversation can choose where it runs on its composer (This checkout, Own worktree or Last worktree), and that choice wins."
    },
    "chat.whileRunning": {
        "type": "string",
        "enum": [
            "steer",
            "queue"
        ],
        "enumDescriptions": [
            "Send it to the agent now: Chat interrupts the turn in flight, Team Work hands it to the operations manager.",
            "Hold it in the conversation's queue and send it when the turn finishes."
        ],
        "default": "steer",
        "description": "What the send button does with a message typed while a turn is running. Ctrl+Enter (⌘+Enter on a Mac) does the other one for that message."
    },
    "chat.autoMerge": {
        "type": "boolean",
        "default": true,
        "description": "Replaced by chat.mergeWhen. While chat.mergeWhen is not set, false here still means the work waits until you press Keep it."
    },
    "chat.mergeWhen": {
        "type": "string",
        "enum": [
            "idle",
            "keep",
            "every-turn"
        ],
        "default": "idle",
        "enumDescriptions": [
            "Once the conversation has been idle for chat.mergeIdleMinutes, or when you press Keep it",
            "Only when you press Keep it",
            "After every turn (nothing is left for Edit from here to put back)"
        ],
        "description": "When a chat agent working in its own worktree has its work committed, synced with the same rebase rules as team worktrees, and merged into the repository's current branch. Until then a turn's files can be put back with Edit from here; a turn already merged cannot. Conflicts are reported in the conversation instead of merged."
    },
    "history.keepDays": {
        "type": "number",
        "default": 30,
        "minimum": 1,
        "maximum": 3650,
        "description": "How many days of agent activity this computer keeps for Agent history and the activity graph. Every event of a session is kept for that long; parts of the history older than this are removed."
    },
    "chat.mergeIdleMinutes": {
        "type": "number",
        "default": 10,
        "minimum": 1,
        "maximum": 1440,
        "description": "With chat.mergeWhen set to idle: how many minutes a conversation sits without a turn before its agent's work is merged."
    },
    "chat.worktreeCleanup": {
        "type": "string",
        "enum": [
            "prompt",
            "auto",
            "manual"
        ],
        "default": "prompt",
        "enumDescriptions": [
            "Ask what to do with the worktree when the agent stops",
            "Remove the worktree automatically (kept if it still has uncommitted changes)",
            "Keep worktrees, to clean up by hand later"
        ],
        "description": "What to do with a chat/solo agent's isolated git worktree when the agent stops — mirrors the team worktree cleanup options."
    },
    "worktrees.setupCommands": {
        "type": "array",
        "items": {
            "type": "string"
        },
        "default": [],
        "description": "Commands to run, in order, in every new agent worktree before the agent works in it — installing dependencies, for example (\"npm ci\"). Each runs in the worktree without a shell unless it uses shell syntax (pipes, &&, variables), which runs through /bin/sh or cmd.exe. Output goes to a log the task links to. They run on your computer, so only commit commands you would run yourself."
    },
    "worktrees.linkIgnoredFiles": {
        "type": "array",
        "items": {
            "type": "string"
        },
        "default": [],
        "description": "Files git ignores that a new agent worktree should share with your own checkout, as globs (\".env*\", \"config/local.json\"): each match is linked into the worktree. A name without a slash matches at any depth. Files git tracks are never replaced."
    },
    "worktrees.setupBlocking": {
        "type": "boolean",
        "default": true,
        "description": "Hold an agent's work until its worktree's setup has finished. When setup fails the task offers Start anyway and Retry. Off: the agent starts at once and setup runs beside it."
    },
    "worktrees.setupTimeoutMinutes": {
        "type": "number",
        "default": 10,
        "minimum": 1,
        "description": "How long one worktree setup command may run before it is stopped and the setup counts as failed."
    },
    "mcp.shareRepoServers": {
        "type": "boolean",
        "default": true,
        "description": "Attach the repository's MCP servers (.vscode/mcp.json and .mcp.json, minus any disabledServers) to every agent session the workbench starts, whatever CLI runs it — Copilot CLI, Claude Code, Codex, Gemini or OpenCode — so instructions and skills that use an MCP tool work across runtimes. Local models on the lite toolset are excluded. VS Code-style ${env:VAR} and ${workspaceFolder} references are expanded; servers needing ${input:…} prompts are skipped."
    },
    "mcp.reportInventory": {
        "type": "boolean",
        "default": true,
        "description": "Tell your organisation which tool servers (MCP servers) this repository's agents are given and which tools each one offers, so it can see what is in use and review it. Sent only when this workbench is signed in to an organisation: each server's name, how it is started or reached as the repository writes it, and its tools' names and descriptions — never a credential's value. To read a server's tool list the workbench starts it once in the background."
    },
    "mcp.connectors": {
        "type": "array",
        "default": [],
        "description": "Your own connectors (Settings → Connectors, \"Just me\"): MCP servers every agent the workbench starts on this computer can use, in every repository. Each is { name, transport: \"http\", url } for a remote server or { name, transport: \"stdio\", command, args, env } for a program on this computer; keys go in env as ${secret:NAME}, never as values. They always go through the workbench's tool gate, which signs in to a remote server for the agents (OAuth) and holds the token. Your organisation can lock this list."
    },
    "mcp.toolPermissions": {
        "type": "object",
        "default": {},
        "description": "What agents may do with each connector's tools (Settings → Connectors → Tool permissions): { <server fingerprint>: { <tool>: \"allow\" | \"ask\" | \"off\" } } — Always allow, Needs approval (held for a person, like any other approval) or Blocked (agents are not shown it). A tool left out gets Needs approval when its server marks it destructive, else Always allow. The workspace policy's tool rules can only make these stricter."
    },
    "customisations.briefAgents": {
        "type": "boolean",
        "default": true,
        "description": "Tell every agent the workbench starts what customisations the repository carries — skills, prompts, agents, instructions and MCP servers, one line each with the path — so a CLI that only reads its own folders can still use the others' on request. Items the CLI discovers by itself are left out, so the briefing costs a few lines, not the files."
    },
    "designSystem.useByDefault": {
        "type": "boolean",
        "default": true,
        "description": "Tell every agent the workbench starts to load the default design system before it makes any interface, page, deck, document or picture: your organisation's, else your team's, else this repository's, else your own (Settings → Design systems). Your organisation's policy may set this; turning the design system off for a repository stops the line there too."
    },
    "designSystem.default": {
        "type": "string",
        "default": "",
        "description": "The design system this repository's agents load first, by its id, when neither your organisation nor your team has a default (Settings → Design systems, Make default for this repository). A repository may commit it; your organisation's policy may lock it."
    },
    "designSystem.personalDefault": {
        "type": "string",
        "default": "",
        "description": "Your own default design system, by its id: the one agents load first in every repository on this computer when your organisation, your team and the repository name none (Settings → Design systems, Make my default)."
    },
    "designSystem.added": {
        "type": "array",
        "default": [],
        "items": {
            "type": "string"
        },
        "description": "Design systems your organisation or team offers that this repository added, by id (Settings → Design systems, Add to this repository): installed for everyone who works in it, while they are offered. A repository may commit it."
    },
    "designSystem.personalAdded": {
        "type": "array",
        "default": [],
        "items": {
            "type": "string"
        },
        "description": "Design systems your organisation or team offers that you added, by id (Settings → Design systems, Add for me): installed in every repository on this computer, while they are offered."
    },
    "skills.turnedOff": {
        "type": "array",
        "default": [],
        "items": {
            "type": "string"
        },
        "description": "Shared skills — built in, your organisation's or your team's — that agents in this repository are not offered. Set with \"Turn off for this repository\" on Settings → Skills; skill_load refuses them here, and agents are not told about them."
    },
    "skills.evaluationJudge": {
        "type": "string",
        "default": "",
        "description": "The model that grades a skill evaluation your organisation asks this machine for, when the request names none: a provider and a model, such as anthropic/claude-sonnet-4-5 or ollama:qwen2.5-coder. Empty = the worker model grades its own answers."
    },
    "skills.draftAfterReviewRounds": {
        "type": "number",
        "default": 3,
        "minimum": 0,
        "maximum": 50,
        "description": "When a task on a board is done after more than this many review rounds, a cheap model drafts a skill from what the reviews taught, with placeholders for names, hosts and ids, and puts it in your Inbox as a suggested skill. Nothing is saved or sent to your organisation until you choose. 0 never drafts one."
    },
    "skills.draftModel": {
        "type": "string",
        "default": "",
        "description": "The model that drafts a suggested skill from a task's reviews: a provider and a model, such as ollama:qwen2.5-coder or anthropic/claude-haiku-4-5. It goes through this machine's model proxy, so the workspace's data protection and budgets apply. Empty = this machine's local model when it is running, else its default model."
    },
    "teams.designer.defaultModels": {
        "type": "object",
        "default": {},
        "properties": {
            "fast": {
                "type": "string",
                "description": "For quick, routine work."
            },
            "standard": {
                "type": "string",
                "description": "For everyday work."
            },
            "strong": {
                "type": "string",
                "description": "For careful judgement work."
            }
        },
        "additionalProperties": false,
        "description": "The model \"Design a specialist\" recommends for each kind of work: fast, standard and strong. Each is a provider and a model, such as anthropic/claude-haiku-4-5 or azure/gpt-5-mini. A kind left empty gets the workbench's own choice. Your organisation can set these for everyone. The project's classification still applies: a model it does not allow is passed over."
    },
    "teams.contextProfile": {
        "type": "string",
        "default": "auto",
        "enum": [
            "auto",
            "lean",
            "standard",
            "full"
        ],
        "description": "How much of each agent's context the workbench fills: Lean (shortest prompts, for small models and tight plans), Standard (the everyday balance) or Full (everything the member can use, for large-context models on an unmetered plan). Automatic chooses per member from its model and the plan it runs on. A member or a team can choose its own; your organisation can set it for everyone and lock it."
    },
    "customisations.allowOwn": {
        "type": "boolean",
        "default": true,
        "description": "Let people add their own customisations from the workbench: create or upload skills, agents, prompts and instructions, share their own into a repository, and upload or create plugins. Turned off, the Add menus offer only what your organisation publishes; what people already have stays where it is. An organisation usually sets this for everyone."
    },
    "plugins.allowedMarketplaces": {
        "type": "array",
        "default": [],
        "items": {
            "type": "string"
        },
        "description": "The plugin marketplaces people may add and install plugins from, each by its name (claude-plugins-official) or by where it is (owner/repo, a git URL or a marketplace.json URL). Empty means any marketplace. Marketplaces your organisation publishes are always allowed."
    },
    "plugins.blocked": {
        "type": "array",
        "default": [],
        "items": {
            "type": "string"
        },
        "description": "Plugins nobody may install or turn on, each as name@marketplace, or a bare name for that plugin from any marketplace. A blocked plugin already installed is shown as blocked and cannot be turned on again from the workbench."
    },
    "hooks.allowed": {
        "type": "string",
        "enum": [
            "everyone",
            "organisation",
            "off"
        ],
        "enumDescriptions": [
            "People may add and change their own hooks, and the organisation's are added too.",
            "Only hooks your organisation publishes are written by the workbench: people add the ones it offers, and the ones it installs are put in place for them. Hooks already in people's files are left as they are.",
            "The workbench writes no hooks and takes away the ones your organisation installed. Hooks already in people's files are left as they are."
        ],
        "default": "everyone",
        "description": "Who may add hooks — commands that run at set points in an agent's work — through the workbench."
    },
    "history.autoArchiveIdleDays": {
        "type": "number",
        "default": 0,
        "minimum": 0,
        "maximum": 365,
        "description": "Archive a Chat, Research or Team Work conversation once it has been idle this many days. Archived conversations are kept and searchable, and leave the main history list; pinned and running ones are never archived. 0 turns it off."
    },
    "teamwork.autoStartQueued": {
        "type": "boolean",
        "default": true,
        "description": "Start the next queued Team Work session automatically when the team's active session finishes. When off, queued sessions wait for a manual start from the queue."
    },
    "teamwork.maxConcurrentPlans": {
        "type": "number",
        "default": 2,
        "minimum": 1,
        "maximum": 8,
        "description": "How many plans one team works on at once. The first runs with the team itself; each other plan run alongside it gets a crew — a copy of the team for that plan, on its own worktrees — and plans beyond the limit wait in the queue. A team whose members all run on local models works one plan at a time whatever this says (the machine is the limit); a team's own Plans at once value overrides both."
    },
    "teamwork.maxConcurrentPerSubscription": {
        "type": "number",
        "default": 3,
        "minimum": 0,
        "maximum": 20,
        "description": "How many Team Work agents may work at once on the same subscription — your Claude, Copilot, Codex or Antigravity sign-in. When more tasks are ready, the rest wait on the board (\"Waiting: 3 of your Claude agents are already working\") and start as others finish; reviews always start. Agents on an API key or a local model are not counted. 0 means no limit."
    },
    "git.mergeStrategy": {
        "type": "string",
        "enum": [
            "direct",
            "pull-request",
            "ask"
        ],
        "default": "ask",
        "enumDescriptions": [
            "Merge the feature branch into the default branch locally and push it",
            "Push the branch and open a pull request (GitHub CLI, falls back to the compare page)",
            "Ask which of the two to use each time"
        ],
        "description": "How a feature branch is merged back to the default branch from the app."
    },
    "git.conflictResolver.mode": {
        "type": "string",
        "enum": [
            "in-session",
            "dedicated"
        ],
        "enumDescriptions": [
            "The agent already responsible (operations manager, else the assignee; the chat agent for chat merges) resolves conflicts in its own session.",
            "Start a one-shot resolver agent in the worktree on the runtime below (your default runtime unless you pick another); it resolves, commits and is stopped. Falls back to in-session when it cannot start or finish."
        ],
        "default": "in-session",
        "description": "Who resolves merge conflicts when a worktree is merged back and the merge stops on conflicts."
    },
    "git.conflictResolver.runtime": {
        "type": "string",
        "enum": [
            "",
            "copilot",
            "native",
            "cline",
            "claude-code",
            "codex",
            "gemini",
            "opencode",
            "kilo",
            "acp-command"
        ],
        "default": "",
        "description": "Agent runtime for the dedicated conflict resolver (same choices as a team member). Empty = your default runtime, on its own sign-in."
    },
    "git.conflictResolver.provider": {
        "type": "string",
        "default": "",
        "description": "Hosted or local provider the resolver's runtime routes through (inference runtimes only). Empty = the runtime's own sign-in. Set it to local-runner to use the bundled local model."
    },
    "git.conflictResolver.endpoint": {
        "type": "string",
        "default": "",
        "description": "OpenAI-compatible endpoint override for the resolver (empty = the provider's default)."
    },
    "git.conflictResolver.model": {
        "type": "string",
        "default": "",
        "description": "Model for the dedicated resolver (empty = the backend's default; the local runner serves one model)."
    },
    "git.conflictResolver.autoApprove": {
        "type": "boolean",
        "default": true,
        "description": "Let the dedicated resolver edit and commit inside the worktree without per-tool approval prompts. Its scope is one in-progress merge in one worktree."
    },
    "git.conflictResolver.timeoutMinutes": {
        "type": "number",
        "default": 10,
        "minimum": 1,
        "maximum": 120,
        "description": "How long the dedicated resolver may take before the in-session fallback runs."
    },
    "chatApprovals.medium": {
        "type": "string",
        "enum": [
            "ask",
            "auto"
        ],
        "default": "ask",
        "description": "Default approval policy for MEDIUM-risk tools (edit · move files) of chat/solo agents that are not part of a team."
    },
    "chatApprovals.high": {
        "type": "string",
        "enum": [
            "ask",
            "auto"
        ],
        "default": "ask",
        "description": "Default approval policy for HIGH-risk tools (delete · execute commands) of chat/solo agents that are not part of a team. Keep on \"ask\" unless you fully trust the repository."
    },
    "facilitator.chatModel": {
        "type": "string",
        "default": "",
        "description": "Model the built-in editor's inline completion uses in its \"always\" mode, at `facilitator.chatEndpoint` (empty = no hosted completion). Chat itself runs on your default runtime or the specialist you pick; this no longer changes it."
    },
    "facilitator.chatProvider": {
        "type": "string",
        "enum": [
            "",
            "ollama",
            "lmstudio",
            "llamacpp",
            "vllm",
            "mlx",
            "foundry-local",
            "azure",
            "bedrock",
            "anthropic",
            "gemini",
            "xai",
            "openrouter",
            "kilo-gateway",
            "groq",
            "together",
            "deepinfra",
            "fireworks",
            "deepseek",
            "mistral",
            "openai-compatible",
            "copilot",
            "native",
            "cline",
            "claude-code",
            "codex",
            "opencode",
            "grok",
            "kilo",
            "acp-command"
        ],
        "default": "copilot",
        "description": "Hosted provider whose stored API key the built-in editor's inline completion sends with requests to `facilitator.chatEndpoint` in its \"always\" mode. Chat itself runs on your default runtime or the specialist you pick; this no longer changes it."
    },
    "facilitator.chatEndpoint": {
        "type": "string",
        "default": "",
        "description": "OpenAI-compatible endpoint the built-in editor's inline completion may use in its \"always\" mode (with `facilitator.chatModel`). Empty = only the local model completes code."
    },
    "facilitator.opsManagerModel": {
        "type": "string",
        "default": "",
        "description": "Model for the task board orchestrator (empty = the runtime's default; a local model needs one)."
    },
    "facilitator.opsManagerRuntime": {
        "type": "string",
        "enum": [
            "",
            "copilot",
            "native",
            "cline",
            "claude-code",
            "codex",
            "gemini",
            "opencode",
            "kilo",
            "acp-command"
        ],
        "enumDescriptions": [
            "Your default runtime (runtimes.default)",
            "GitHub Copilot CLI",
            "Built-in agent: comes with AI Workbench and runs any model server — local or hosted — through the proxy",
            "Cline in ACP mode",
            "Claude Code through its ACP adapter",
            "OpenAI Codex through its ACP adapter",
            "Google Antigravity through its official ACP server",
            "OpenCode in ACP mode",
            "Kilo Code in ACP mode",
            "Custom ACP agent command (needs mjuWorkbench.enableCustomAcpAgents)"
        ],
        "default": "",
        "description": "Task board orchestrator: the runtime the agent that plans, hands out and reviews a team's tasks runs on when the team's operations manager does not name a runtime of its own, and the runtime the team's Facilitator chats on. Empty = your default runtime. Specialists and team members that name a runtime keep theirs."
    },
    "facilitator.opsManagerProvider": {
        "type": "string",
        "enum": [
            "",
            "ollama",
            "lmstudio",
            "llamacpp",
            "vllm",
            "mlx",
            "foundry-local",
            "azure",
            "bedrock",
            "anthropic",
            "gemini",
            "xai",
            "openrouter",
            "kilo-gateway",
            "groq",
            "together",
            "deepinfra",
            "fireworks",
            "deepseek",
            "mistral",
            "openai-compatible",
            "copilot",
            "native",
            "cline",
            "claude-code",
            "codex",
            "opencode",
            "grok",
            "kilo",
            "acp-command"
        ],
        "default": "copilot",
        "description": "The model source of the task board orchestrator (`facilitator.opsManagerRuntime`): for the built-in agent and other runtimes that take one, the provider whose key and base URL it routes through (`local-runner` = the local model on this computer). Empty or `copilot` = the runtime's own sign-in, or your default runtime when no orchestrator runtime is set."
    },
    "localRunner.enabled": {
        "type": "boolean",
        "default": true,
        "description": "Allow the bundled local model runner (a llama.cpp server the app downloads into its private storage and supervises). Off stops it and hides it from providers."
    },
    "localModel.declined": {
        "type": "boolean",
        "default": false,
        "description": "You said you don't want a local model on this computer. Nothing is downloaded, the local model service stays off, and the workbench stops suggesting one. Choose a local model in Settings → Setup to change your mind."
    },
    "localRunner.model": {
        "type": "string",
        "default": "qwen2.5-1.5b-instruct",
        "description": "Which model the local runner serves — a catalogue id, or the id of a model you added from Hugging Face or a local file. Changing it downloads the new model on the next start. Settings → Local models lists what is available."
    },
    "localRunner.port": {
        "type": "number",
        "default": 8087,
        "minimum": 1024,
        "maximum": 65535,
        "description": "Loopback port the local runner listens on (http://127.0.0.1:<port>/v1)."
    },
    "localRunner.contextLength": {
        "type": "number",
        "default": 0,
        "minimum": 0,
        "maximum": 1048576,
        "description": "Context window the local runner starts the model with (0 = the model's own default). Larger costs RAM. A per-model override in Settings → Local models wins over this."
    },
    "localRunner.threads": {
        "type": "number",
        "default": 0,
        "minimum": 0,
        "maximum": 256,
        "description": "CPU threads for the local runner (0 = llama.cpp decides)."
    },
    "localRunner.autoStart": {
        "type": "boolean",
        "default": true,
        "description": "Start the local runner when the workbench activates, once its build and model are downloaded."
    },
    "localRunner.startOnRequest": {
        "type": "boolean",
        "default": true,
        "description": "Start a downloaded model the first time an agent asks for it by name, beside whatever is already running. Off means an agent's request for a model that is not running fails with a message, rather than starting it or being answered by another model."
    },
    "localRunner.acceleration": {
        "type": "string",
        "enum": [
            "auto",
            "gpu",
            "cpu",
            "vulkan",
            "rocm",
            "cuda"
        ],
        "enumDescriptions": [
            "Detect the GPU and use the matching build: Vulkan on Linux / Windows with a Vulkan loader, Metal on Apple Silicon, CPU otherwise (and always CPU under WSL2).",
            "Use the default GPU build even when detection is unsure — Vulkan on Linux / Windows, Metal on any Mac.",
            "CPU build only.",
            "The Vulkan build: NVIDIA, AMD and Intel GPUs through the driver's Vulkan runtime.",
            "The ROCm (HIP) build for AMD GPUs. Often faster than Vulkan where the card is supported; a large download.",
            "The CUDA build for NVIDIA GPUs, with the CUDA runtime. Usually the fastest on NVIDIA; a large download."
        ],
        "default": "auto",
        "description": "Compute backend for the local runner. Settings → Local models → Engine shows what was detected and which builds this machine can use; a backend that is not published for this platform falls back to Automatic."
    },
    "localRunner.allowBuildUpdates": {
        "type": "boolean",
        "default": true,
        "description": "Let the local runner ask GitHub for llama.cpp builds at all. Off means the build this version of the app was tested with, whatever localRunner.build says — for computers that may not contact GitHub. Every build is verified against the SHA-256 GitHub publishes."
    },
    "localRunner.build": {
        "type": "string",
        "default": "latest",
        "markdownDescription": "Which llama.cpp build runs the local model. `latest` (the default) follows the newest official build — downloaded from the ggml-org release and checked against the SHA-256 GitHub publishes — so new model families load without waiting for an app update; if a new build will not start, the local model goes back to the tested one. `tested` stays on the build this version of Mojo Up was tested with. A build number such as `b10850` keeps that one build."
    },
    "localRunner.gpuLayers": {
        "type": "number",
        "default": 99,
        "minimum": 0,
        "maximum": 999,
        "description": "Model layers offloaded to the GPU when accelerated (llama-server -ngl); 99 = everything. Lower it if the model does not fit in GPU memory."
    },
    "localRunner.device": {
        "type": "string",
        "default": "",
        "markdownDescription": "Which GPU runs the local model, as llama.cpp names it (`llama-server --list-devices`: `Vulkan0`, `CUDA1`, `ROCm0`, `MTL0`). Empty is Automatic: with more than one GPU it picks a discrete card over an integrated one, so a laptop's NVIDIA or Radeon card is used rather than the Intel or AMD graphics built into the processor. `all` splits the model over every GPU, llama.cpp's own default. A device that is no longer there falls back to Automatic; a `--device` in `localRunner.extraArgs` wins."
    },
    "localRunner.extraArgs": {
        "type": "string",
        "default": "",
        "description": "Extra llama-server flags, space separated, passed verbatim (advanced — e.g. \"-ngl 99\" to offload to a GPU build you swapped in)."
    },
    "localRunner.serverPath": {
        "type": "string",
        "default": "",
        "markdownDescription": "Absolute path to a `llama-server` you built yourself, used instead of the pinned llama.cpp build. The workbench still supervises it, health-checks it and shows its log. Needed for GGUFs stock llama.cpp cannot load — a new architecture, or an out-of-tree tensor format such as a ROCm FP4 build. Leave empty to use the bundled build."
    },
    "localRunner.mlxEndpoint": {
        "type": "string",
        "default": "",
        "markdownDescription": "Apple silicon only: where `mlx_lm.server` (the MLX model server from the `mlx-lm` package) answers, including `/v1`. Leave empty for its default, http://127.0.0.1:8080/v1. Agents reach it as the `mlx` provider."
    },
    "localRunner.huggingFace.enabled": {
        "type": "boolean",
        "default": true,
        "description": "Allow searching Hugging Face and downloading GGUF models from it in Settings → Local models. Downloads are pinned to a commit and verified against the hash the Hub reports. Off hides the browser and leaves only the built-in catalogue."
    },
    "localRunner.huggingFace.allowGated": {
        "type": "boolean",
        "default": false,
        "description": "Allow installing from gated or private Hugging Face repositories using the stored access token. Off refuses them, so a token cannot be spent without asking."
    },
    "localRunner.approvedModelsOnly": {
        "type": "boolean",
        "default": false,
        "description": "When your organisation asks this machine to download a local model, take only models on the organisation's approved list. Off downloads one that is not on the list and says so in the answer; on Mojo Up AI Cloud an unlisted model is always refused."
    },
    "taskBoard.store": {
        "type": "string",
        "enum": [
            "auto",
            "local",
            "organisation",
            "hub"
        ],
        "default": "auto",
        "enumDescriptions": [
            "The organisation decides: an enterprise project's roots use its shared board, a personal project's board stays in .mojoup/taskboard.",
            "The board lives in this repository's .mojoup/taskboard.",
            "The board is the organisation project's shared board in AIOE: every member's workbench sees and changes the same tasks. Needs the repository's .mojoup/project.json to name an organisation project and repository, and this workbench signed in to the organisation.",
            "The board is kept by your hub: every workbench and node paired to it sees and changes the same tasks. Needs the project shared with the team on the hub."
        ],
        "description": "Where this repository's task board lives. By default the organisation decides: once a project is an enterprise project (or a personal one is promoted), every member's clone uses its shared board."
    },
    "tasks.backend": {
        "type": "string",
        "enum": [
            "local",
            "github",
            "azure-boards"
        ],
        "default": "local",
        "enumDescriptions": [
            "Tasks live only in .mojoup/taskboard (this repository).",
            "Tasks are mirrored to GitHub Issues (your GitHub sign-in).",
            "Tasks are mirrored to Azure Boards work items (your Azure DevOps sign-in)."
        ],
        "description": "Where team/solo tasks are stored. The local board is always the working store; a backend mirrors it."
    },
    "tasks.azureDevOps.organisation": {
        "type": "string",
        "default": "",
        "description": "Azure DevOps organisation for the Azure Boards mirror (empty = derive from the origin remote)."
    },
    "tasks.azureDevOps.project": {
        "type": "string",
        "default": "",
        "description": "Azure DevOps project for the Azure Boards mirror (empty = derive from the origin remote)."
    },
    "tasks.azureDevOps.workItemType": {
        "type": "string",
        "default": "Task",
        "description": "Work item type created for a mirrored task (Task, User Story, Issue, Product Backlog Item…)."
    },
    "tasks.archiveDoneAfterDays": {
        "type": "number",
        "default": 14,
        "minimum": 0,
        "maximum": 365,
        "description": "Finished tasks leave the board for the archive after this many days in Done, so the Done column stays short. A task something unfinished depends on, one with an open review, one whose work is not merged yet, a part of a project task that is not done, and a task you restored from the archive all stay. 0 keeps finished tasks on the board until you archive them yourself."
    },
    "tasks.archiveHousekeepingAfterMinutes": {
        "type": "number",
        "default": 60,
        "minimum": 0,
        "maximum": 10080,
        "description": "Housekeeping tasks (what a routine or a request to the scribe puts on the board) leave it for the archive this many minutes after they finish successfully. One that failed, or finished with concerns, stays as long as any other task. 0 treats housekeeping like any other task."
    },
    "tasks.wakeAfterMinutes": {
        "type": "number",
        "default": 10,
        "minimum": 0,
        "maximum": 240,
        "description": "How long an agent has to pick up a task handed to it before it is reminded. If it still has not, after `tasks.wakeReminders` reminders the task goes to the team's operations manager and then to you, this long between each step. Every step is in the activity log and the audit trail. 0 turns reminders off."
    },
    "tasks.wakeReminders": {
        "type": "number",
        "default": 2,
        "minimum": 0,
        "maximum": 10,
        "description": "How many times an agent is reminded of a task it has not picked up before the task goes to the team's operations manager. A reminder points at the task; it never sends the task again."
    },
    "pipelines.azureDevOps.organisation": {
        "type": "string",
        "default": "",
        "description": "Azure DevOps organisation whose Azure Pipelines build this repository. Empty = derive it from a dev.azure.com origin remote. Set this and the project for a GitHub repository built by Azure Pipelines."
    },
    "pipelines.azureDevOps.project": {
        "type": "string",
        "default": "",
        "description": "Azure DevOps project whose Azure Pipelines build this repository. Empty = derive it from a dev.azure.com origin remote."
    },
    "pullRequests.settleTasksOnMerge": {
        "type": "boolean",
        "default": true,
        "description": "When a pull request joined to a task merges, move the task to done with a comment saying so. Off, the task only gets the comment. A pull request closed without merging never moves its task."
    },
    "pullRequests.host": {
        "type": "string",
        "enum": [
            "auto",
            "gitlab",
            "forgejo",
            "gitea",
            "bitbucket"
        ],
        "enumDescriptions": [
            "Tell the service from the origin remote's address (github.com, dev.azure.com, gitlab.com, codeberg.org, gitea.com, bitbucket.org, or a server named gitlab., forgejo. or gitea.)",
            "A GitLab server",
            "A Forgejo server",
            "A Gitea server",
            "Bitbucket Cloud"
        ],
        "default": "auto",
        "description": "Which service this repository's origin is on, for a self-managed server whose address does not say. Pull requests are read and acted on through that service's API."
    },
    "pullRequests.apiUrl": {
        "type": "string",
        "default": "",
        "description": "The web address of a self-managed GitLab, Forgejo or Gitea server (https://git.example.com), when it is not the origin remote's server as written: a different port, or a server under a subpath. Empty = from the origin remote."
    },
    "agents.idleStop": {
        "type": "string",
        "enum": [
            "automation",
            "all",
            "off"
        ],
        "enumDescriptions": [
            "Stop agents that a routine, the daily review or a task filed for a specialist started, once they sit idle.",
            "Stop every idle agent outside a running team as well: chats and specialists you started yourself.",
            "Never stop an agent for being idle."
        ],
        "default": "automation",
        "description": "Which agents are stopped after sitting idle for `agents.idleStopMinutes`. Members of a running team are never stopped this way; an agent waiting for your approval, or with a task still in progress, is left alone."
    },
    "agents.idleStopMinutes": {
        "type": "number",
        "default": 10,
        "minimum": 0,
        "maximum": 1440,
        "description": "How long an agent may sit idle before `agents.idleStop` stops it. 0 turns it off."
    },
    "agents.stuckAfterMinutes": {
        "type": "number",
        "default": 20,
        "minimum": 0,
        "maximum": 1440,
        "description": "An agent that sits idle, or waits on you, for this long while it holds a task in progress is shown as Stuck on its card, in Mission Control and on the node's dashboard. 0 never calls an agent stuck."
    },
    "review.specialist": {
        "type": "string",
        "default": "",
        "description": "The specialist (its id on the Teams page) that writes the daily review when the routine or the Run button names nobody. Empty = the default chat agent, started for the review and stopped after it."
    },
    "review.strongerAttempt": {
        "type": "boolean",
        "default": true,
        "description": "When a team's reviewer keeps sending a task back and it would go to you for review, a stronger model first has one look: what it says to change goes back to the implementer for one last round. Once per task, only when the project's classification allows that model's provider and the budget allows it; what was tried is said on the task. Off = the task comes to you straight away."
    },
    "review.strongerModel": {
        "type": "string",
        "default": "",
        "description": "The stronger model that has one look at a task before it comes to you: a provider and a model, such as anthropic/claude-opus-4-1. It goes through this machine's model proxy. Empty = the team's operations manager's own model, when it runs through a provider rather than a CLI's own sign-in."
    },
    "chat.defaultSpecialist": {
        "type": "string",
        "default": "",
        "description": "The specialist (its id on the Teams page) a new chat starts with, pinned from the Agent menu in Chat's composer. Empty, or a specialist this project does not have = the first specialist."
    },
    "routines.defaultSpecialist": {
        "type": "string",
        "default": "",
        "description": "The specialist (its id on the Teams page) that runs any routine that names nobody: a prompt routine talks to it in Chat, and the daily review is written by it unless `review.specialist` names someone else. Empty = the default chat agent from Settings → Models → Chat & Team Work."
    },
    "pipelines.agentRuns": {
        "type": "string",
        "enum": [
            "ask",
            "allow",
            "off"
        ],
        "enumDescriptions": [
            "Ask each time an agent wants to start, re-run or cancel a pipeline run; no answer means no.",
            "Let agents start, re-run and cancel runs without asking (they are still on the audit log).",
            "Agents may read pipelines and logs but never start, re-run or cancel a run."
        ],
        "default": "ask",
        "description": "Whether agents may start, re-run or cancel GitHub Actions and Azure Pipelines runs. Reading runs and logs is always allowed; approving a deployment is never an agent's call."
    },
    "pipelines.notifyOnFinish": {
        "type": "boolean",
        "default": true,
        "description": "Say when a GitHub Actions or Azure Pipelines run the Pipelines view saw running has finished, or has started waiting for your approval, with a button to open it."
    },
    "github.oauthClientId": {
        "type": "string",
        "default": "",
        "description": "Client ID of the GitHub OAuth App used for the in-app device-flow sign-in. Empty = sign in through the GitHub CLI (gh) only."
    },
    "git.defaultBranch": {
        "type": "string",
        "default": "main",
        "description": "Branch name for repositories the workbench initialises."
    },
    "devcontainer.reopenMode": {
        "type": "string",
        "enum": [
            "replace",
            "add"
        ],
        "default": "replace",
        "enumDescriptions": [
            "Opening a repository in its dev container replaces the local copy with the one in the container.",
            "The copy in the container is added to the project beside the local one."
        ],
        "description": "What happens when a repository is opened in its dev container."
    },
    "ui.developerViews": {
        "type": "boolean",
        "default": false,
        "description": "Show the developer-facing Debug view. (Architecture is a view like any other and is always in the sidebar.)"
    },
    "tasks.githubRepo": {
        "type": "string",
        "default": "",
        "description": "GitHub repository for issue sync as \"owner/repo\" (empty = derive from the origin remote)."
    },
    "ops.maxDispatchesPerTask": {
        "type": "number",
        "default": 3,
        "minimum": 1,
        "maximum": 10,
        "description": "Maximum times an ops run may (re)dispatch the same task before flagging it needs-human-review."
    },
    "ops.stallTimeoutMinutes": {
        "type": "number",
        "default": 30,
        "minimum": 5,
        "maximum": 240,
        "description": "End an ops run with a timeout result when the task board sees no activity for this many minutes."
    },
    "plans.unifyWaitHours": {
        "type": "number",
        "default": 24,
        "minimum": 0,
        "maximum": 8760,
        "markdownDescription": "When a plan's work is finished but some of its acceptance criteria are not confirmed, the plan waits this many hours for someone to pass or fail them (or close it anyway) before it closes on its own, recording them as not confirmed. `0` never waits: the plan closes at once and says which criteria were not confirmed."
    },
    "ops.shapeByDefault": {
        "type": "boolean",
        "default": false,
        "description": "Start new Team Work conversations with \"Shape first\" on: the operations manager coaches you through a brief (type, sections, decisions, phases) before anything is planned. The toggle beside the team picker still overrides it per conversation."
    },
    "ops.testBeforeCommit": {
        "type": "string",
        "enum": [
            "off",
            "advisory"
        ],
        "enumDescriptions": [
            "Do not run anything; task_done commits as before.",
            "Run the tests covering the task's changed files, record the result on the task and tell the reviewer — then commit either way."
        ],
        "default": "advisory",
        "description": "Run the tests that cover a task's changed files when task_done commits its work. Advisory always commits: a failure is reported to the reviewer, not held against the agent. A suite that could not run — no node_modules, no test file for the changed unit, a timeout — is recorded as indeterminate and never counted as a failure. There is deliberately no blocking mode yet; see docs/plans/2026-09-task-done-test-gate.md."
    },
    "memory.enabled": {
        "type": "boolean",
        "default": true,
        "description": "Master switch for the graph memory. Off: nothing is provisioned or indexed, agents are not briefed on memory tools and the memory tools answer 'disabled'. Git hooks already installed stay until you remove them from the Memory view."
    },
    "memory.graphifyPath": {
        "type": "string",
        "default": "",
        "description": "Path to the graphify CLI executable. Leave empty for auto-detection (PATH, then a private environment managed by the app)."
    },
    "memory.autoProvision": {
        "type": "boolean",
        "default": true,
        "description": "Automatically install the Graphify memory engine (PyPI package 'graphifyy') into a private Python environment the workbench manages, when it is not found on PATH."
    },
    "memory.autoBuild": {
        "type": "boolean",
        "default": true,
        "description": "Build the knowledge graph automatically (local AST pass — free, offline) when the repository has none."
    },
    "memory.autoInstallGitHooks": {
        "type": "boolean",
        "default": true,
        "description": "Install graphify's post-commit/post-checkout rebuild hooks and the graph.json union-merge driver automatically, so the memory graph stays current across the whole git lifecycle."
    },
    "memory.autoInstallSkill": {
        "type": "boolean",
        "default": false,
        "description": "Retired and ignored. Earlier versions copied the graphify skill into the repository's assistant folders; agents started by the workbench are now given the project's memory directly, and nothing is installed. The key is kept so settings files that carry it stay valid."
    },
    "memory.semanticBackend": {
        "type": "string",
        "enum": [
            "default-runtime",
            "auto",
            "env",
            "lmstudio",
            "ollama",
            "unsloth",
            "local-runner",
            "claude-code",
            "codex",
            "copilot",
            "antigravity",
            "azure",
            "bedrock",
            "anthropic",
            "gemini",
            "xai",
            "openrouter",
            "groq",
            "together",
            "deepinfra",
            "fireworks",
            "deepseek",
            "mistral",
            "openai-compatible",
            "off"
        ],
        "default": "default-runtime",
        "enumDescriptions": [
            "Your default runtime reads them, on its own sign-in (Claude Code, Codex, Copilot CLI or Antigravity) — what your agents already use, at the cost you already pay. A runtime that cannot read documents leaves memory to code only and says so.",
            "Automatic: a key in the environment, a local model or local server that is running, then a provider key stored in the workbench, else code only. Can use a local model; the default does not.",
            "Only use an API key from the environment (GEMINI_API_KEY, ANTHROPIC_API_KEY, OPENAI_API_KEY, …).",
            "LM Studio server (uses mjuWorkbench.lmstudioEndpoint; first loaded model unless semanticModel is set).",
            "Ollama server (uses mjuWorkbench.ollamaEndpoint; first available model unless semanticModel is set).",
            "Unsloth Desktop server (uses mjuWorkbench.unslothEndpoint and the API key stored in the workbench, which it requires on every request; first available model unless semanticModel is set).",
            "The bundled local model runner (llama.cpp) the workbench downloads and serves itself — set up in Settings → Setup. Uses the model it is serving unless semanticModel is set.",
            "Claude Code, on its own sign-in (a Pro / Max plan) — no API key. One chunk at a time through the `claude` CLI; semanticModel optional (e.g. sonnet, haiku).",
            "OpenAI Codex, on its own sign-in — no API key. Each chunk is one turn of the CLI, so it is slower than an API; semanticModel optional.",
            "GitHub Copilot CLI, on its own sign-in — no API key. Each chunk is one turn of the CLI and spends premium requests; semanticModel optional.",
            "Google Antigravity, on its own sign-in — no API key. Each chunk is one turn of the CLI; semanticModel optional.",
            "Azure OpenAI / AI Foundry — needs mjuWorkbench.azure.endpoint and semanticModel.",
            "Amazon Bedrock — the region from mjuWorkbench.bedrock.region, a Bedrock API key and semanticModel (a Bedrock model id).",
            "Anthropic Claude, using the key stored in the workbench. semanticModel optional.",
            "Google Gemini, using the key stored in the workbench. semanticModel optional.",
            "xAI (Grok) — an xAI API key and semanticModel.",
            "OpenRouter, using the key stored in the workbench. semanticModel required.",
            "Groq, using the key stored in the workbench. semanticModel required.",
            "Together AI, using the key stored in the workbench. semanticModel required.",
            "DeepInfra, using the key stored in the workbench. semanticModel required.",
            "Fireworks AI, using the key stored in the workbench. semanticModel required.",
            "DeepSeek, using the key stored in the workbench. semanticModel required.",
            "Mistral AI, using the key stored in the workbench. semanticModel required.",
            "Custom OpenAI-compatible server — needs an endpoint and semanticModel.",
            "Never run the semantic pass — index code only (docs/PDFs/images are skipped)."
        ],
        "description": "Who reads the docs, PDFs and images for the knowledge graph. Code is always mapped on this computer, for free. The default is your default runtime, on its own sign-in; a hosted provider uses the key stored in the workbench (Settings → Models); a local model keeps everything on this computer but needs one set up."
    },
    "memory.semanticModel": {
        "type": "string",
        "default": "",
        "markdownDescription": "Model for the semantic pass. Empty = the first model the local server reports (LM Studio / Ollama), or the backend's own default (Anthropic / Gemini). Every other cloud provider needs this set explicitly. Use [Select Memory Semantic Backend & Model](command:mjuWorkbench.selectMemorySemanticModel) to pick from a live model list."
    },
    "git.branchPrefix": {
        "type": "string",
        "default": "feat/",
        "description": "The start of the name of the branch each approved plan gets: its work merges there, and you merge it into main or open a pull request when the plan is finished. With feat/, a plan to add sales quotes works on feat/add-sales-quotes. A plan made while you are on a branch of your own uses that branch instead."
    },
    "browser.recordingFormat": {
        "type": "string",
        "enum": [
            "webm",
            "mp4"
        ],
        "default": "webm",
        "enumDescriptions": [
            "WebM (VP9). Plays in every browser and in most players; always available.",
            "MP4 (H.264). Plays everywhere, including in chat and mail clients that refuse WebM. Encoded by the app's own Chromium; falls back to WebM, and says so, on a build that cannot."
        ],
        "description": "The container the built-in browser's recordings are written in — from the toolbar's Record button and from an agent's browser_record_start, which can still ask for the other."
    },
    "browser.recordingFps": {
        "type": "number",
        "default": 5,
        "minimum": 1,
        "maximum": 15,
        "description": "Frames per second for recordings. Five is enough to show a feature working; more makes larger files and heavier captures."
    },
    "browser.homePage": {
        "type": "string",
        "default": "",
        "description": "The page a new tab in the built-in browser opens on. Empty opens a blank tab. A web address, or anything the address bar takes."
    },
    "browser.searchEngine": {
        "type": "string",
        "enum": [
            "duckduckgo",
            "google",
            "bing",
            "brave"
        ],
        "default": "duckduckgo",
        "enumDescriptions": [
            "DuckDuckGo.",
            "Google.",
            "Bing.",
            "Brave Search."
        ],
        "description": "Where the built-in browser's address bar sends something that is not a web address."
    },
    "browser.defaultEmulation": {
        "type": "string",
        "default": "",
        "description": "A device new tabs start as — a device preset's id such as iphone-15 or laptop-1366, or one you saved. Empty starts them at the pane's own size."
    },
    "browser.historyRetentionDays": {
        "type": "number",
        "default": 90,
        "minimum": 0,
        "maximum": 3650,
        "description": "How many days the built-in browser keeps the pages you visited. 0 keeps no history at all. Each project keeps its own history, on this computer only — never in a repository."
    },
    "browser.agentsShareStorage": {
        "type": "boolean",
        "default": true,
        "description": "Agents' tabs in the built-in browser use your sign-ins and site storage in the same project. Off gives agents a storage of their own in each project, so they start signed out and never see your cookies. Either way one project's browser storage is never another's."
    },
    "research.artifacts.formats": {
        "type": "array",
        "items": {
            "type": "string",
            "enum": [
                "okf",
                "md",
                "html"
            ]
        },
        "default": [
            "okf",
            "md",
            "html"
        ],
        "description": "How a finished research report is written to disk. 'okf' is the Library's typed Markdown (indexed by memory and read there); 'md' is plain Markdown with notebook-friendly front matter (what Obsidian and Gemini Notebook receive); 'html' is a self-contained page anyone can open in a browser."
    },
    "research.reviewTasks": {
        "type": "boolean",
        "default": false,
        "description": "Hold the tasks a research run files for your review before they are marked done. Off (the default): they go straight to Done and the report is the thing to read. A specialist set to wait for your review still waits."
    },
    "research.artifacts.folder": {
        "type": "string",
        "default": "docs/research",
        "description": "Folder, relative to the repository, that research artifacts are written into."
    },
    "docs.folders": {
        "type": "array",
        "items": {
            "type": "string"
        },
        "default": [
            "docs"
        ],
        "description": "Folders, relative to the repository, whose Markdown files the Library lists. READMEs at the top level, agent instruction files, ADR folders, team roles and research notes are always documents."
    },
    "docs.defaultFormat": {
        "type": "string",
        "enum": [
            "okf",
            "markdown"
        ],
        "enumDescriptions": [
            "Typed Markdown: front matter and [kind] section tags, so memory can index sections and the view can check the structure.",
            "Plain Markdown with no marker or section tags."
        ],
        "default": "okf",
        "description": "The format a new document is written in. Existing files keep whatever they are; 'Save as OKF' converts a plain one."
    },
    "docs.templatesFolder": {
        "type": "string",
        "default": "docs/templates",
        "description": "Folder, relative to the repository, whose Markdown files are the templates the Library offers for new documents — each with 'template:' (its name), 'summary:' and 'path:' in its front matter. The Library lists the folder as Templates so they can be edited in place."
    },
    "research.critique.enabled": {
        "type": "boolean",
        "default": false,
        "description": "Review every research report before it is delivered: a second model critiques the draft and the author revises it (Critique mode). The Research composer can switch this per run."
    },
    "research.critique.reviewer": {
        "type": "string",
        "default": "",
        "description": "The specialist (its id in Teams) that reviews a research specialist's draft when the specialist names no reviewer of its own. Empty means the specialist reviews its own draft in a fresh frame."
    },
    "research.obsidian.enabled": {
        "type": "boolean",
        "default": false,
        "description": "Offer to send research reports to an Obsidian vault on this machine."
    },
    "research.obsidian.vaultPath": {
        "type": "string",
        "default": "",
        "description": "The Obsidian vault folder, as the workbench sees it where the repository lives (a repository in WSL needs a WSL path)."
    },
    "research.obsidian.vaultName": {
        "type": "string",
        "default": "",
        "description": "The vault's name as Obsidian shows it, for the link that opens a note. Empty uses the folder's name."
    },
    "research.obsidian.folder": {
        "type": "string",
        "default": "Research",
        "description": "Folder inside the vault that research notes are written into."
    },
    "research.obsidian.projectFolder": {
        "type": "string",
        "default": "",
        "description": "The folder inside the vault this project's notes live in — research reports, the knowledge memory's community notes, routers. Empty means 'AI Workbench/<repository folder name>'. The workbench writes nothing outside it, and inside it only the notes it generated; your Inbox/ there is read, never written."
    },
    "research.obsidian.mirrorDocs": {
        "type": "boolean",
        "default": true,
        "description": "Copy the repository's own documents into the vault's Docs/ folder when knowledge memory is written there, so a community note links to something you can read in the vault instead of naming a repository path. They are copies: edit the repository, not the vault. Off for a repository whose docs are too large to want in a vault."
    },
    "research.obsidian.syncKnowledge": {
        "type": "boolean",
        "default": false,
        "description": "Write knowledge memory into the vault after every memory update — community notes, routers, lessons and the work log — as well as when you press Sync in the Memory view."
    },
    "research.obsidian.openAfterExport": {
        "type": "boolean",
        "default": true,
        "description": "Open the note in Obsidian after sending it."
    },
    "research.obsidian.auto": {
        "type": "boolean",
        "default": false,
        "description": "Send every finished research report to Obsidian without being asked."
    },
    "git.pushAfterLanding": {
        "type": "boolean",
        "default": false,
        "description": "Push the branch approved work merges into — the branch your checkout is on, main or a feature branch — to 'origin' after the merge. When nobody sets it, an organisation's project pushes (so the others see the work) and any other project does not: merging is local, so that branch is ahead of the remote until you push. Set it, on or off, and that wins. A branch the workspace policy protects is never pushed, and a failed push is reported and never blocks the merge."
    },
    "git.autoFetchMinutes": {
        "type": "number",
        "default": 5,
        "minimum": 0,
        "maximum": 1440,
        "description": "Fetch 'origin' in the background at most this often while Source Control is in use, so ahead/behind counts against the remote are current. 0 turns the automatic fetch off; Source Control's fetch and sync actions still work."
    },
    "git.worktreeBasePath": {
        "type": "string",
        "default": "",
        "description": "Base directory for Git worktrees. When empty, worktrees are created in a sibling '.mojoup-worktrees' folder next to the repository root"
    },
    "git.autoCleanup": {
        "type": "boolean",
        "default": true,
        "description": "Automatically remove Git worktrees when team agents stop. When false, worktrees persist and must be removed manually"
    },
    "git.useFeatureBranches": {
        "type": "boolean",
        "default": false,
        "description": "Create a feature branch per task instead of working directly on a rebased default branch"
    },
    "git.branchNamePattern": {
        "type": "string",
        "default": "feature/{teamId}/{taskId}",
        "description": "Branch name template; supports {teamId}, {taskId}, {agentName} placeholders"
    },
    "ui.workspaceView": {
        "type": "string",
        "enum": [
            "office",
            "cards"
        ],
        "enumDescriptions": [
            "The pixel-art office: agents at desks, animated by what they do.",
            "Agent cards: who each agent is, their board task, what they are doing now and recently."
        ],
        "default": "cards",
        "scope": "window",
        "description": "How the Workspace view shows running agents."
    },
    "ui.designSystem": {
        "type": "string",
        "enum": [
            "glass",
            "solid"
        ],
        "enumDescriptions": [
            "Mojo Up (Glass): translucent surfaces over a soft backdrop.",
            "Mojo Up (Solid): the opaque teal-on-mouve system MojoFlow uses."
        ],
        "default": "glass",
        "scope": "window",
        "description": "Which Mojo Up theme the workbench uses."
    },
    "usage.liveLimits": {
        "type": "boolean",
        "default": true,
        "scope": "window",
        "description": "Ask each signed-in provider for its own usage limits (Claude Code's five-hour and weekly windows, an OpenRouter balance) using the credential that CLI already stores. Turn off to show only what this workbench metered itself."
    },
    "editor.inlineCompletion": {
        "type": "string",
        "enum": [
            "off",
            "local",
            "always"
        ],
        "enumDescriptions": [
            "No inline suggestions in the built-in editor.",
            "Only ever complete with a local model — the bundled runner, or a local endpoint. Never spends money.",
            "Also use the configured chat model, including a hosted one."
        ],
        "default": "local",
        "scope": "window",
        "description": "Whether the built-in editor suggests code as you type, and which model may do it."
    },
    "editor.openFilesIn": {
        "type": "string",
        "enum": [
            "built-in",
            "external"
        ],
        "enumDescriptions": [
            "Open repository files in the workbench's own editor, in the right pane. The editor's toolbar still offers your external editor for any file.",
            "Hand every file to the external editor chosen under Settings → Projects & Editors."
        ],
        "default": "built-in",
        "description": "Where a file opens when something in the workbench opens one — a doc, a customisation, the workspace policy, a pipeline definition. Files outside the repository always go to the external editor."
    },
    "localModel.share": {
        "type": "boolean",
        "default": true,
        "scope": "window",
        "markdownDescription": "Let repositories in a WSL distro or container on this computer use the model this computer already serves — on Windows, the GPU-backed runner for every distro, checked for reachability from each one first. A repository whose workbench serves its own model keeps using it."
    },
    "terminal.shell": {
        "type": "string",
        "default": "",
        "scope": "machine",
        "markdownDescription": "The shell the integrated terminal starts: a path or a name on PATH (`pwsh.exe`, `cmd.exe`, `/usr/bin/fish`). Empty uses `$SHELL` — PowerShell on Windows. A terminal for a repository in WSL runs the distro's own login shell; set this for that distro to change it."
    },
    "terminal.profiles": {
        "type": "array",
        "default": [],
        "scope": "window",
        "items": {
            "type": "object",
            "required": [
                "label",
                "command"
            ],
            "properties": {
                "id": {
                    "type": "string",
                    "description": "Stable id; derived from the label when omitted."
                },
                "label": {
                    "type": "string",
                    "description": "What the profile is called in the new-terminal menu and on its tabs."
                },
                "command": {
                    "type": "string",
                    "description": "Typed into a fresh login shell in the root's folder, e.g. `claude --model opus` or `npm run dev`."
                },
                "cwd": {
                    "type": "string",
                    "description": "Folder to start in; the root's folder when omitted."
                },
                "colour": {
                    "type": "string",
                    "enum": [
                        "red",
                        "orange",
                        "yellow",
                        "green",
                        "cyan",
                        "blue",
                        "purple",
                        "pink"
                    ],
                    "description": "The colour tabs from this profile start with."
                }
            }
        },
        "markdownDescription": "Your own terminal profiles, beside the shell and the agent CLIs: a name, the command to run and a tab colour. Edited from the new-terminal menu's *Manage profiles…*."
    },
    "terminal.fontSize": {
        "type": "number",
        "default": 13,
        "minimum": 8,
        "maximum": 32,
        "scope": "window",
        "description": "Font size of the integrated terminal, in pixels."
    },
    "terminal.scrollback": {
        "type": "number",
        "default": 5000,
        "minimum": 100,
        "maximum": 100000,
        "scope": "window",
        "description": "Lines the integrated terminal keeps above the visible screen."
    },
    "terminal.keepRunning": {
        "type": "boolean",
        "default": false,
        "scope": "window",
        "markdownDescription": "Keep new terminals running after you close them. A closed terminal moves to *Running in the background*, where you can bring it back. On Linux, macOS, WSL, SSH hosts and dev containers that have `tmux`, it also keeps running after the app quits and comes back on the next start; on Windows, and where there is no tmux, it runs until the app quits. Each terminal can also be switched from its tab's menu."
    },
    "terminal.keepRunningTmuxKeys": {
        "type": "boolean",
        "default": false,
        "scope": "window",
        "markdownDescription": "For people who know tmux: in terminals kept in tmux, keep tmux's own status line and its Ctrl+B prefix key. Off, a kept terminal looks and types like any other (the mouse wheel scrolls back)."
    },
    "terminal.agentControls": {
        "type": "boolean",
        "default": true,
        "scope": "window",
        "markdownDescription": "Open Claude Code, Codex and GitHub Copilot CLI from the terminal menu with the workbench's controls: the repository's connectors through the tool gate and, where the CLI allows it, every tool call checked against your workspace policy and every prompt put through data protection. Your own files are never changed: the controls go only to the terminal the workbench opens. Off, the menu types the CLI's name into a plain shell. When your organisation requires the controls (`terminals.requireControls` in the policy), they apply whatever this says, and a CLI that cannot have its tool calls and prompts checked here is not opened."
    },
    "remote.mirror.enabled": {
        "type": "boolean",
        "default": false,
        "scope": "application",
        "markdownDescription": "Let a paired client with the `ui:view` scope watch this window over the Control API, for remote debugging. Off. While anyone is watching, a badge in the title bar says so and will not stop saying so."
    },
    "remote.mirror.allowControl": {
        "type": "boolean",
        "default": false,
        "scope": "application",
        "markdownDescription": "Also let a client with the `ui:control` scope click and type into this window. Off, and worth leaving off unless someone is actively debugging with you — it is full control of the app, and the window shows a warning the whole time it is possible."
    },
    "updates.source": {
        "type": "string",
        "default": "",
        "scope": "application",
        "markdownDescription": "Where the app looks for updates. Empty uses the Mojo Up portal. A folder — a share, a synced directory, `C:\\builds` — holding your installers and a `latest.json` beside them, or an `http(s)` URL serving the same document. Use this to test a build before it is published."
    },
    "updates.autoDownload": {
        "type": "boolean",
        "default": true,
        "scope": "application",
        "markdownDescription": "Download a new version as soon as one is found, so updating is one click. Nothing installs until you say so. Off, the Updates pane offers a Download button instead. Your organisation's command to update or roll back installs either way."
    },
    "updates.notify": {
        "type": "boolean",
        "default": true,
        "scope": "application",
        "markdownDescription": "Tell you when a version is ready: a notification once per version, and a dot on the app's tray and taskbar icon until you install it. Off, the Updates pane still shows it."
    },
    "updates.allowUnsigned": {
        "type": "boolean",
        "default": false,
        "scope": "application",
        "markdownDescription": "Install an update whose Authenticode signature does not check out. Off, and only worth turning on for unsigned builds from your own update source — every such install is logged as a warning."
    },
    "backup.schedule": {
        "type": "string",
        "default": "daily",
        "enum": [
            "off",
            "daily",
            "weekly"
        ],
        "scope": "application",
        "markdownDescription": "How often the workbench backs itself up on this computer: your settings, your projects and each repository's chat history and runs. Daily by default; the copies go to the backups folder and the oldest are removed after `backup.keep`."
    },
    "backup.keep": {
        "type": "number",
        "default": 7,
        "minimum": 1,
        "maximum": 100,
        "scope": "application",
        "markdownDescription": "How many automatic backups to keep. Older ones are removed; backups you made yourself are never removed."
    },
    "backup.folder": {
        "type": "string",
        "default": "",
        "scope": "application",
        "markdownDescription": "Where automatic backups are written. Empty uses the `backups` folder in the app's own data. A synced folder (OneDrive, Dropbox) keeps them off this computer too."
    },
    "backup.transcripts": {
        "type": "boolean",
        "default": false,
        "scope": "application",
        "markdownDescription": "Also back up agents' full transcripts (at most 64 MB per repository, newest first). Off: chat history and runs are backed up, transcripts stay on this computer."
    },
    "backup.organisation": {
        "type": "boolean",
        "default": true,
        "scope": "application",
        "markdownDescription": "When you are signed in to an organisation that keeps backups, send it a copy after each backup: your settings, projects and recent chat history (never keys or tokens, at most 16 MB). A new computer can then be restored after you sign in."
    },
    "product.tier": {
        "type": "string",
        "enum": [
            "free",
            "teams",
            "enterprise"
        ],
        "default": "free",
        "scope": "application",
        "description": "Which edition this workbench runs as, worked out by the workbench from what it is signed in to: Enterprise with an organisation's own platform, Teams with Mojo Up AI Cloud Teams (Mojo Up's SaaS control plane for the AI Workbench) or a hub on your own network, Free otherwise. It is not a choice: a value written here is put back."
    },
    "product.features": {
        "type": "array",
        "items": {
            "type": "string"
        },
        "default": [],
        "scope": "application",
        "description": "What this workbench is licensed for (nodes, mesh, backups, …), from the device licence its platform issued or the edition's defaults. Worked out by the workbench, like product.tier: a value written here is put back."
    },
    "teams.hub.enabled": {
        "type": "boolean",
        "default": false,
        "scope": "application",
        "description": "Make this workbench your hub (Teams edition): the other workbenches, nodes and phones of your team pair to it and share its projects, task boards and inbox over your own network."
    },
    "identity.name": {
        "type": "string",
        "default": "",
        "scope": "application",
        "description": "Your name, as your work is signed: on a project's roadmap, tasks and activity, and on comments you leave. Empty: your computer's user name. When you join a team or sign in to your organisation, the name they know you by is used there."
    },
    "identity.email": {
        "type": "string",
        "default": "",
        "scope": "application",
        "description": "Your email address, kept with your name on the projects this computer keeps. It is how your past work is matched to you when a project is later shared with a team or your organisation. Optional."
    },
    "teams.hub.name": {
        "type": "string",
        "default": "",
        "scope": "application",
        "description": "Your team's name, as the hub shows it to the people who join."
    },
    "teams.hub.advertise": {
        "type": "string",
        "default": "",
        "scope": "application",
        "description": "A host name or address other people's devices use to reach this hub instead of one of this computer's own — a DNS name, or a router that forwards the hub's port. Add the port after a colon when the router forwards a different one (hub.example.com:8443; an IPv6 address in brackets, [2001:db8::1]:8443); without one, the hub's own port. Invitations carry it. Empty: the address of the network the hub is on."
    },
    "teams.hub.url": {
        "type": "string",
        "default": "",
        "scope": "application",
        "description": "The address of your hub this workbench has joined. Filled in when you join with an invitation; empty when this workbench is not part of a hub."
    },
    "teams.hub.fingerprint": {
        "type": "string",
        "default": "",
        "scope": "application",
        "description": "The fingerprint of your hub's certificate (sha256:…), checked on every connection so this workbench only ever talks to your own hub. Filled in when you join."
    },
    "notifications.os": {
        "type": "string",
        "default": "unfocused",
        "enum": [
            "unfocused",
            "always",
            "never"
        ],
        "enumDescriptions": [
            "Only when the app is not the window you are in — minimised, behind something else, or in the tray.",
            "Whenever it happens, even while you are looking at the workbench.",
            "Never; everything stays inside the app."
        ],
        "scope": "application",
        "markdownDescription": "When the operating system should tell you about an agent waiting on a permission, an update, a repository's workbench that stopped, or a warning from one, while you were elsewhere."
    },
    "notifications.quietHours": {
        "type": "string",
        "default": "",
        "pattern": "^$|^([01]\\d|2[0-3]):[0-5]\\d-([01]\\d|2[0-3]):[0-5]\\d$",
        "scope": "application",
        "markdownDescription": "Quiet hours, as `HH:MM-HH:MM` in this computer's local time (`22:00-07:00` runs across midnight). Empty means none. During quiet hours anything that needs you still arrives in the inbox and counts on the bell, but the operating system stays silent — except for an approval someone asked of you by name, which still reaches you. While you are signed in to an organisation whose platform holds your quiet hours (set on its console), those replace these and this setting waits; signed out, it applies again."
    },
    "notifications.quietDays": {
        "type": "array",
        "default": [],
        "items": {
            "type": "string",
            "enum": [
                "monday",
                "tuesday",
                "wednesday",
                "thursday",
                "friday",
                "saturday",
                "sunday"
            ]
        },
        "scope": "application",
        "markdownDescription": "Whole days that are quiet, such as the weekend. They work like quiet hours all day long: the inbox and the bell still fill, the operating system stays silent, and an approval asked of you by name still reaches you."
    },
    "notifications.muted": {
        "type": "array",
        "default": [],
        "items": {
            "type": "string"
        },
        "scope": "application",
        "markdownDescription": "Projects you have muted, by their id on this desktop. A muted project's notifications still arrive in the inbox, but they never raise an operating-system notification and do not count on the bell."
    },
    "updates.checkIntervalHours": {
        "type": "number",
        "default": 24,
        "minimum": 0,
        "maximum": 720,
        "scope": "application",
        "markdownDescription": "How often the app checks for a new version, in hours. `0` turns the periodic check off; the Updates pane can still check on demand."
    },
    "updates.channel": {
        "type": "string",
        "default": "stable",
        "enum": [
            "stable",
            "nightly"
        ],
        "enumDescriptions": [
            "Released versions only.",
            "Nightly builds of what will become the next version, as they come out. Switching back to stable offers the newest release, even when it is older than the nightly you have."
        ],
        "scope": "application",
        "markdownDescription": "Which versions the app updates to. An update must match the channel; the app only installs an older version when you switch channel. `MOJOUP_DISABLE_AUTO_UPDATE=1` in the app's environment turns updates off on this machine."
    },
    "teams.default": {
        "type": "string",
        "default": "",
        "scope": "window",
        "markdownDescription": "The team this project works with by default, by id: the team Home and the team wizard show it first. Set it for the whole project, or for one repository in its committed settings. Empty: no default."
    },
    "projects.startTeamsForParts": {
        "type": "boolean",
        "default": true,
        "markdownDescription": "When another repository of the project sends this one part of its plan, start the team (or the specialist) the part is given to, so it is worked without anyone pressing Start. The part goes to the team named in the plan, else the team with the same id as the one that planned it, else one whose roles cover the part's role, else the specialist for that role, else this repository's own tasks. Off: the part waits on that team's board until the team is started."
    },
    "teams.definitions": {
        "type": "array",
        "default": [],
        "scope": "window",
        "items": {
            "type": "object"
        },
        "markdownDescription": "Team definitions stored outside the repository: saved for this workbench they are available to every project on this computer; saved for a project, to every repository in it. Managed from the Teams view; a repository's own teams live in `.mojoup/teams/`."
    },
    "ui.colorMode": {
        "type": "string",
        "enum": [
            "auto",
            "light",
            "dark"
        ],
        "enumDescriptions": [
            "Follow the VS Code colour theme.",
            "Always light.",
            "Always dark."
        ],
        "default": "auto",
        "scope": "window",
        "description": "Light or dark, or follow the system’s theme."
    },
    "appearance.reduceEffects": {
        "type": "boolean",
        "default": false,
        "scope": "window",
        "description": "Turn off the moving background shapes and other decoration that is only there to look nice"
    },
    "ui.location": {
        "type": "string",
        "enum": [
            "sidebar",
            "panel"
        ],
        "default": "sidebar",
        "description": "Not used by the desktop app, which owns its own window; kept so a value from an older settings file is recognised and ignored."
    },
    "workspace.activeFolder": {
        "type": "string",
        "default": "",
        "scope": "window",
        "description": "Not used by the desktop app, which opens one repository per window; kept so a value from an older settings file is recognised and ignored."
    },
    "azure.endpoint": {
        "type": "string",
        "default": "",
        "description": "Azure OpenAI / AI Foundry resource endpoint, e.g. https://<resource>.openai.azure.com (normalised to the v1 surface). Used by members whose provider is \"azure\"."
    },
    "azure.auth": {
        "type": "string",
        "enum": [
            "key",
            "entra"
        ],
        "default": "key",
        "enumDescriptions": [
            "An API key stored in the operating system’s keychain (Settings → Inference gateways → Azure OpenAI / AI Foundry).",
            "A Microsoft Entra token from VS Code's Microsoft account — nothing to store."
        ],
        "description": "How requests to Azure authenticate."
    },
    "bedrock.region": {
        "type": "string",
        "default": "us-east-1",
        "description": "AWS region whose Amazon Bedrock endpoint members with provider \"bedrock\" use, e.g. ap-southeast-2. The key is an Amazon Bedrock API key (Settings → Models), sent as a bearer token — no SigV4 signing."
    },
    "bedrock.endpoint": {
        "type": "string",
        "enum": [
            "runtime",
            "mantle"
        ],
        "default": "runtime",
        "enumDescriptions": [
            "bedrock-runtime.<region>.amazonaws.com/openai/v1 — AWS's recommendation. Chat only: the model id is typed (an inference profile such as us.anthropic.claude-…) because this endpoint cannot list models.",
            "bedrock-mantle.<region>.api.aws/v1 — the compatibility endpoint: lists models, and carries what runtime does not yet."
        ],
        "description": "Which of Bedrock's two OpenAI-compatible endpoints to use."
    },
    "media.images.provider": {
        "type": "string",
        "default": "",
        "description": "What makes AI images (covers, styled pictures, illustrations): an API provider you have a key for (azure, openai-compatible, bedrock, gemini, together, fireworks, openrouter, xai), your ChatGPT plan through Codex (codex), the image runner the workbench downloads (image-runner: stable-diffusion.cpp on this computer, nothing leaves the machine; Settings › Local models › Images), or an image server on this computer (local-images). Empty: the first one that is ready. Your organisation's policy may turn AI images off or limit the providers."
    },
    "media.images.model": {
        "type": "string",
        "default": "",
        "description": "The image model (on Azure, the deployment's name). Empty: the provider's usual image model."
    },
    "media.images.credential": {
        "type": "string",
        "default": "",
        "description": "Which of the provider's named keys makes images. Empty: the provider's own key. A named key that is missing is missing — it never falls back to the provider's own."
    },
    "media.images.endpoint": {
        "type": "string",
        "default": "",
        "description": "For an OpenAI-compatible service only: its address, including /v1 (https://api.openai.com/v1 for OpenAI itself). A named key's own address wins."
    },
    "media.images.local": {
        "type": "string",
        "default": "",
        "description": "The address of an image server on this computer or your network — ComfyUI, stable-diffusion.cpp's sd-server, LocalAI or Ollama (http://localhost:8188). Empty: the usual local ports are tried. Nothing is bundled; images made there cost nothing and are what a local-only policy allows."
    },
    "foundryLocal.endpoint": {
        "type": "string",
        "default": "",
        "description": "Foundry Local OpenAI-compatible endpoint (including /v1). Found from the running service; leave empty to use the default port."
    },
    "runtimes.copilot.enabled": {
        "type": "boolean",
        "default": true,
        "description": "Enable the Copilot runtime for new agent sessions, terminal profiles and Usage. Disable to hide it without uninstalling it or stopping active sessions."
    },
    "runtimes.native.enabled": {
        "type": "boolean",
        "default": true,
        "description": "Enable the native runtime for new agent sessions, terminal profiles and Usage. Disable to hide it without uninstalling it or stopping active sessions."
    },
    "runtimes.cline.enabled": {
        "type": "boolean",
        "default": true,
        "description": "Enable the cline runtime for new agent sessions, terminal profiles and Usage. Disable to hide it without uninstalling it or stopping active sessions."
    },
    "runtimes.claude-code.enabled": {
        "type": "boolean",
        "default": true,
        "description": "Enable the claude-code runtime for new agent sessions, terminal profiles and Usage. Disable to hide it without uninstalling it or stopping active sessions."
    },
    "runtimes.codex.enabled": {
        "type": "boolean",
        "default": true,
        "description": "Enable the codex runtime for new agent sessions, terminal profiles and Usage. Disable to hide it without uninstalling it or stopping active sessions."
    },
    "runtimes.gemini.enabled": {
        "type": "boolean",
        "default": true,
        "description": "Enable the Google Antigravity runtime for new agent sessions, terminal profiles and Usage. Disable to hide it without uninstalling it or stopping active sessions."
    },
    "runtimes.opencode.enabled": {
        "type": "boolean",
        "default": true,
        "description": "Enable the opencode runtime for new agent sessions, terminal profiles and Usage. Disable to hide it without uninstalling it or stopping active sessions."
    },
    "runtimes.grok.enabled": {
        "type": "boolean",
        "default": true,
        "description": "Enable the Grok Build runtime for new agent sessions, terminal profiles and Usage. Disable to hide it without uninstalling it or stopping active sessions."
    },
    "runtimes.kilo.enabled": {
        "type": "boolean",
        "default": true,
        "description": "Enable the Kilo Code runtime for new agent sessions, terminal profiles and Usage. Disable to hide it without uninstalling it or stopping active sessions."
    },
    "runtimes.copilot.command": {
        "type": "string",
        "default": "",
        "description": "Command (or full path) for the GitHub Copilot CLI runtime. Empty uses \"copilot\" from PATH."
    },
    "runtimes.cline.command": {
        "type": "string",
        "default": "",
        "description": "Command (or full path) for the Cline runtime. Empty uses \"cline\" from PATH."
    },
    "runtimes.claude-code.command": {
        "type": "string",
        "default": "",
        "description": "Command (or full path) for the Claude Code ACP adapter. Empty uses \"claude-agent-acp\" (or the older \"claude-code-acp\") from PATH, and falls back to \"npx -y @agentclientprotocol/claude-agent-acp\" when neither is installed but Claude Code is."
    },
    "runtimes.codex.command": {
        "type": "string",
        "default": "",
        "description": "Command (or full path) for the Codex ACP adapter. Empty uses \"codex-acp\" from PATH, and falls back to \"npx -y @agentclientprotocol/codex-acp\" when Codex is installed without it."
    },
    "runtimes.gemini.command": {
        "type": "string",
        "default": "",
        "description": "Command (or full path) for the official Antigravity ACP server. Empty uses the server installed by Settings; legacy Gemini defaults migrate automatically."
    },
    "runtimes.opencode.command": {
        "type": "string",
        "default": "",
        "description": "Command (or full path) for the OpenCode runtime. Empty uses \"opencode\" from PATH."
    },
    "runtimes.grok.command": {
        "type": "string",
        "default": "",
        "description": "Command (or full path) for the Grok Build runtime. Empty uses \"grok\" from PATH."
    },
    "runtimes.kilo.command": {
        "type": "string",
        "default": "",
        "description": "Command (or full path) for the Kilo Code runtime. Empty uses \"kilo\" from PATH."
    },
    "runtimes.default": {
        "type": "string",
        "enum": [
            "copilot",
            "claude-code",
            "codex",
            "opencode",
            "kilo",
            "gemini",
            "grok",
            "cline",
            "native"
        ],
        "enumDescriptions": [
            "GitHub Copilot CLI",
            "Claude Code",
            "OpenAI Codex",
            "OpenCode",
            "Kilo Code",
            "Google Antigravity",
            "Grok Build",
            "Cline",
            "Built-in agent, with a local model or a hosted one (runtimes.defaultProvider)"
        ],
        "default": "copilot",
        "description": "The runtime agents use unless they choose another: new team members and specialists, Chat when you pick nobody, and the task board orchestrator while its own runtime setting is empty. Existing members keep the runtime they were saved with."
    },
    "runtimes.defaultProvider": {
        "type": "string",
        "default": "",
        "description": "The local or hosted provider the default runtime's model comes from, for runtimes that take one (the built-in agent, Cline, Claude Code, Codex, Kilo Code): \"local-runner\" for the bundled local model, \"ollama\", or a hosted provider id such as \"openrouter\" whose key is stored under Models. Empty = the runtime's own sign-in; the built-in agent then uses the bundled local model."
    },
    "runtimes.defaultModel": {
        "type": "string",
        "default": "",
        "description": "The model the default runtime asks its provider for (empty = the provider's or runtime's default; the bundled local model serves one model)."
    },
    "memory.knowledge.sources": {
        "type": "array",
        "items": {
            "type": "string"
        },
        "default": [],
        "description": "Extra folders whose files belong to knowledge memory wherever they are — an Obsidian project subtree, a notes folder. Absolute paths as the workbench sees them where the repository lives (a repository in WSL needs a WSL path), or paths relative to the repository. Repository docs, ADRs, plans, research, instruction files and every Markdown or text file are knowledge already."
    },
    "memory.automation": {
        "type": "string",
        "enum": [
            "watcher",
            "hooks",
            "off"
        ],
        "default": "watcher",
        "enumDescriptions": [
            "The workbench watches the repository and re-indexes changed files (nothing is written to .git).",
            "graphify's git hooks and merge driver keep the graph current across commits and checkouts (writes to .git/hooks and .git/info/attributes).",
            "Update the graph manually or from the memory tools only."
        ],
        "description": "How the memory graph stays current."
    },
    "memory.semanticSchedule": {
        "type": "string",
        "enum": [
            "nightly",
            "manual"
        ],
        "default": "nightly",
        "enumDescriptions": [
            "Once a night (between 1 and 6 in the morning, when the workbench is running), documents changed since the last pass are read with the memory model. Agents' updates and the file watcher only refresh code, which costs nothing.",
            "Only when you press Rebuild or Update in the Memory view."
        ],
        "description": "When the memory model reads changed documents. Code is always kept current for free; reading documents uses the model chosen for memory and can cost money."
    },
    "memory.shareGraph": {
        "type": "boolean",
        "default": false,
        "description": "Share the memory map with the repository: graphify-out/graph.json is committed (nothing else in graphify-out/), and a merge driver in .git/info/attributes joins two people's maps instead of leaving a conflict. Off: the map stays on this computer and no merge driver is installed. An organisation's project does not need it: its memory is kept with the organisation, labelled and filtered by each person's clearance; turning it off offers to stop tracking a graph.json already committed."
    },
    "ops.perTaskTokenBudget": {
        "type": "number",
        "default": 0,
        "minimum": 0,
        "description": "Tokens a single task may consume before it is stopped and flagged over-budget (0 = off). The inference policy's budgets.perTaskTokens takes precedence when set."
    },
    "localContext.handoff": {
        "type": "boolean",
        "default": true,
        "description": "Before compacting a full context window, ask the agent for a handoff note (kept on the task and in memory) so the fresh context starts from where it stopped."
    },
    "dictation.enabled": {
        "type": "string",
        "enum": [
            "auto",
            "on",
            "off"
        ],
        "enumDescriptions": [
            "Start the dictation endpoint whenever MojoFlow is installed on this machine (its settings folder exists).",
            "Always start the endpoint.",
            "Never start it."
        ],
        "default": "auto",
        "scope": "window",
        "description": "Let MojoFlow dictate into Chat and Team Work: a loopback Control API endpoint for companion apps plus a discovery file MojoFlow finds on its own (under WSL also in the Windows profile). Older true/false values still work as on/off."
    },
    "dictation.port": {
        "type": "number",
        "default": 7433,
        "scope": "window",
        "description": "Loopback port for the dictation companion endpoint (0 = random). Must be reachable from Windows when the workbench runs in WSL, which localhost forwarding does by default."
    },
    "remote.ipc.enabled": {
        "type": "boolean",
        "default": false,
        "scope": "window",
        "description": "Serve the Control API on a local IPC endpoint (Unix socket / named pipe, owner-only) for command-line tools on this machine, such as mojoup-ctl, with full access. MojoFlow dictation does not use it: it has its own port (dictation.enabled). The token is written to the workbench's own storage for the repository (remote/ipc.json there), never into the repository."
    },
    "remote.lan.enabled": {
        "type": "boolean",
        "default": false,
        "scope": "window",
        "description": "Serve the Control API on a network interface for paired devices. Requires a TLS certificate unless plain HTTP is explicitly allowed."
    },
    "remote.lan.host": {
        "type": "string",
        "default": "127.0.0.1",
        "scope": "window",
        "description": "Interface the LAN listener binds to (0.0.0.0 for every interface)."
    },
    "remote.lan.port": {
        "type": "number",
        "default": 7431,
        "scope": "window",
        "description": "Port for the LAN listener (0 = random)."
    },
    "remote.lan.certPath": {
        "type": "string",
        "default": "",
        "scope": "window",
        "description": "PEM certificate for the LAN listener (TLS)."
    },
    "remote.lan.keyPath": {
        "type": "string",
        "default": "",
        "scope": "window",
        "description": "PEM private key for the LAN listener (TLS)."
    },
    "remote.lan.allowPlainHttp": {
        "type": "boolean",
        "default": false,
        "scope": "window",
        "description": "Allow the LAN listener without TLS. Only for trusted networks: tokens travel in the clear."
    },
    "remote.lan.interface": {
        "type": "string",
        "default": "",
        "scope": "window",
        "description": "Which network the listener for paired devices uses: \"tailnet\" for your private network (the mesh), the name of a network interface, or empty to use the address in remote.lan.host. This is the address other workbenches and nodes use to reach this one."
    },
    "remote.lan.autoTls": {
        "type": "boolean",
        "default": true,
        "scope": "window",
        "description": "When the listener for paired devices is reachable from other machines and no certificate is set, make one for this workbench, so the connection is encrypted. Devices that pair check its fingerprint."
    },
    "remote.relay.url": {
        "type": "string",
        "default": "",
        "scope": "window",
        "description": "Relay / platform URL the workbench dials out to (no inbound ports). Your organisation sets it when you sign in."
    },
    "remote.sessionControl.allSessions": {
        "type": "boolean",
        "default": false,
        "scope": "window",
        "markdownDescription": "Let you control every session of yours from the web and the phone — see it live, send the next message, answer approvals and stop it — through your organisation, signed in as you. Off: you turn it on one session at a time, from the session's menu (**Control from the web and phone**). Only you can control your sessions; a routine's run, a task Microsoft 365 Copilot asked for and a cloud agent's run are never included. The kill switch in the tray ends every remote control at once and turns this off."
    },
    "remote.rateLimitPerMinute": {
        "type": "number",
        "default": 120,
        "minimum": 10,
        "scope": "window",
        "description": "Control API requests per minute allowed per remote client."
    },
    "remote.pairing.idleDays": {
        "type": "number",
        "default": 30,
        "minimum": 0,
        "scope": "window",
        "description": "How many days a paired device may go unused before its pairing expires and it has to be paired again. Each use starts the count again. 0 means pairings never expire."
    },
    "observability.otlpEndpoint": {
        "type": "string",
        "default": "",
        "scope": "window",
        "description": "OTLP/HTTP collector base URL (e.g. http://localhost:4318). Every gateway request is exported as a span with tokens, cost, repairs and policy decisions. Empty = off."
    },
    "observability.otlpHeaders": {
        "type": "object",
        "default": {},
        "additionalProperties": {
            "type": "string"
        },
        "scope": "window",
        "description": "Extra headers for the OTLP collector (e.g. an Authorization header)."
    },
    "gateway.mode": {
        "type": "string",
        "enum": [
            "embedded",
            "sidecar",
            "external"
        ],
        "default": "embedded",
        "scope": "window",
        "enumDescriptions": [
            "The inference gateway runs inside the workbench for this repository (stops when it restarts).",
            "A supervised gateway process that survives restarts and can serve other tools on this computer (Claude Code, every runtime that takes a model source, curl).",
            "Route hosted providers to an external OpenAI-compatible gateway such as LiteLLM (mjuWorkbench.gateway.externalUrl); local providers stay direct."
        ],
        "description": "Where the inference gateway runs."
    },
    "gateway.port": {
        "type": "number",
        "default": 7432,
        "scope": "window",
        "description": "Loopback port for the gateway sidecar (0 = random)."
    },
    "gateway.externalUrl": {
        "type": "string",
        "default": "",
        "scope": "window",
        "description": "External gateway base URL including /v1 (e.g. http://localhost:4000/v1 for LiteLLM). Its API key goes in the \"Gateway (OpenAI-compatible)\" row under Settings → Inference gateways."
    },
    "platform.url": {
        "type": "string",
        "default": "",
        "scope": "window",
        "description": "Default platform URL offered when onboarding a project (.mojoup/project.json)."
    },
    "platform.organisation": {
        "type": "string",
        "default": "",
        "scope": "window",
        "description": "Display name of the organisation this desktop is signed in to (set by discovery; shown as \"Managed by <organisation>\" where the platform's policy applies)."
    },
    "platform.cloudUrl": {
        "type": "string",
        "default": "https://service.mojoup.ai",
        "scope": "application",
        "description": "Where \"Sign in to Mojo Up AI Cloud\" finds Cloud. Change it only to try a Cloud that is not the public one (a local one while developing it)."
    },
    "organisation.bindTokensToDevice": {
        "type": "boolean",
        "default": true,
        "scope": "application",
        "description": "When your organisation supports it, bind this computer's sign-in to a key that never leaves it, so a copy of its token is useless anywhere else. Takes effect at the next sign-in or token refresh. Turning it off stops new bindings; a sign-in already bound stays bound until you sign out."
    },
    "platform.mesh.enabled": {
        "type": "boolean",
        "default": true,
        "scope": "application",
        "description": "Once signed in to an organisation that offers a mesh, join it: the desktop runs the bundled aioe-mesh agent so the console and your other machines reach this workbench through the encrypted mesh tunnel instead of the relay. Turning it off stops the agent; the platform marks the node offline and falls back to the relay."
    },
    "platform.mesh.proxy": {
        "type": "string",
        "default": "",
        "scope": "application",
        "description": "The proxy the mesh agent reaches your organisation through. Empty uses the system's proxy for the platform's address (PAC and WPAD included), which is what a network that lets HTTPS out only through its proxy needs; \"direct\" uses none; or a proxy URL such as http://proxy.corp:8080 or socks5://host:1080. Stored as a plain setting, so do not put a password in it."
    },
    "platform.mesh.port": {
        "type": "number",
        "default": 7434,
        "scope": "application",
        "description": "Loopback port the desktop serves signed remote control on for the mesh agent (0 = any free port). The agent exposes it on the node's overlay address at the same port."
    },
    "node.stateDir": {
        "type": "string",
        "default": "",
        "scope": "application",
        "description": "Headless node only (mojoup-node): where the node keeps its settings, projects, secrets, key file, mesh state and status.json. Read from /etc/mojoup/node.json (%ProgramData%\\MojoUp\\node.json on Windows) or the MOJOUP_NODE_STATE_DIR environment variable; empty uses ~/.mojoup/node, or /var/lib/mojoup/node when the node runs as root."
    },
    "node.home": {
        "type": "string",
        "default": "",
        "scope": "application",
        "description": "Headless node only (mojoup-node): the folder the node clones repositories into and runs agent runtimes from, reported to the organisation as the node's home. Empty uses the node's user home directory."
    },
    "node.name": {
        "type": "string",
        "default": "",
        "scope": "application",
        "description": "Headless node only: the name the organisation lists this node under (mojoup-node join --name). Empty uses the hostname."
    },
    "node.tags": {
        "type": "array",
        "items": {
            "type": "string"
        },
        "default": [],
        "scope": "application",
        "description": "Headless node only: mesh policy tags the node asks for when it joins and runs (tag:gpu, tag:linux). The approver may change them; assignment happens in the organisation's console, never here."
    },
    "node.owner": {
        "type": "string",
        "enum": [
            "org",
            "user",
            "project"
        ],
        "default": "org",
        "scope": "application",
        "description": "Headless node only: who the node is offered as belonging to in its manifest — the organisation (a shared node), the person who joins it (their own box) or one project. The approver decides; this is what the node proposes."
    },
    "node.roles": {
        "type": "array",
        "items": {
            "type": "string",
            "enum": [
                "teams",
                "inference",
                "dlp",
                "sandbox",
                "computer"
            ]
        },
        "default": [
            "teams"
        ],
        "scope": "application",
        "description": "Headless node only: what the node is offered for, beside what it detects. teams is always offered; inference and dlp are added when the local runner serves a model (dlp when it serves the model a repository's DLP classifier names); sandbox and computer are offered only when listed here and configured under node.sandbox.* and node.computer.*."
    },
    "node.maxConcurrentAgents": {
        "type": "number",
        "default": 0,
        "minimum": 0,
        "scope": "application",
        "description": "Headless node only: the most agents the node runs at once, as offered in its manifest (0 = no limit offered; the organisation's assignment quota still applies)."
    },
    "node.acceptsDispatchFrom": {
        "type": "array",
        "items": {
            "type": "string"
        },
        "default": [],
        "scope": "application",
        "description": "Headless node only: whom the node itself accepts work from (project:<id>, group:<name>, user:<id>, tenant). Empty leaves it to the organisation's assignment. A node can narrow its audience here, never widen it."
    },
    "node.approvalTimeoutSeconds": {
        "type": "number",
        "default": 300,
        "minimum": 10,
        "maximum": 3600,
        "scope": "application",
        "description": "Headless node only: how long a tool-permission request waits for a remote approval (console, phone, a paired desktop) before the node denies it. Nobody answering means no."
    },
    "node.approvalHoldSeconds": {
        "type": "number",
        "default": 1800,
        "minimum": 10,
        "maximum": 3600,
        "scope": "application",
        "description": "Headless node only: how long a tool-permission request is held once it has reached the organisation's inbox (the person's phone and console) before the node denies it. Never shorter than the timeout above."
    },
    "mesh.autoUpdate": {
        "type": "string",
        "default": "install",
        "enum": [
            "off",
            "notify",
            "install"
        ],
        "enumDescriptions": [
            "Never fetch the mesh agent from the release feed: run the one this build carries, if any.",
            "Check the feed with the app's own update check and say when a newer agent is out; install it from the Updates pane.",
            "Check and install a newer agent as soon as the platform offers it to this machine, restarting the agent; the previous one is put back if the new one does not start."
        ],
        "scope": "application",
        "description": "How the organisation's mesh agent (aioe-mesh) keeps itself current. It is fetched from the platform this machine is signed in to — Mojo Up AI Cloud, or an Enterprise organisation's own, which offers each ring the version it approved — only when that organisation offers a mesh and the Mesh switch is on; a machine deployed without a mesh never fetches one."
    },
    "node.autoUpdate": {
        "type": "string",
        "default": "notify",
        "enum": [
            "off",
            "notify",
            "install"
        ],
        "enumDescriptions": [
            "Never look for a new version by itself.",
            "Look every few hours and say so in the node's log and `mojoup-node status`.",
            "Look every few hours and install a new version: it is tried first, and the node stays on the version it has if the new one does not start."
        ],
        "scope": "application",
        "markdownDescription": "Headless node only: whether the node looks for a new version from where it was installed, and installs it. `MOJOUP_DISABLE_AUTO_UPDATE=1` in the node's environment turns this off whatever it says."
    },
    "node.display.theme": {
        "type": "string",
        "default": "auto",
        "enum": [
            "auto",
            "light",
            "dark"
        ],
        "enumDescriptions": [
            "Ask the terminal (COLORFGBG, a colour query) and take dark when it does not say.",
            "The terminal's background is light: darker colours for muted text.",
            "The terminal's background is dark."
        ],
        "scope": "application",
        "markdownDescription": "Headless node only: the terminal's background for `mojoup-node`'s screens, saved on this node (Preferences in its command palette). `MOJOUP_NODE_THEME` wins for the run it is set in."
    },
    "node.display.mono": {
        "type": "string",
        "default": "auto",
        "enum": [
            "auto",
            "on",
            "off"
        ],
        "enumDescriptions": [
            "Plain ASCII where the terminal cannot show the glyphs (a Linux console, an old Windows console, a locale that is not UTF-8).",
            "Always plain ASCII: no banner, no QR code, no special glyphs.",
            "Always the full glyphs."
        ],
        "scope": "application",
        "markdownDescription": "Headless node only: whether `mojoup-node`'s screens draw in plain ASCII, saved on this node (Preferences). `--mono` and `MOJOUP_NODE_MONO` win for the run they are given in."
    },
    "node.display.screen": {
        "type": "string",
        "default": "auto",
        "enum": [
            "auto",
            "scrollback",
            "fullscreen"
        ],
        "enumDescriptions": [
            "Over SSH the REPL writes into the terminal's own scrollback; at a desk it takes the whole screen.",
            "Always write into the terminal's own scrollback, above a small live footer.",
            "Always take the whole screen."
        ],
        "scope": "application",
        "markdownDescription": "Headless node only: how `mojoup-node`'s REPL uses the terminal, saved on this node (Preferences). `--scrollback`, `--fullscreen` and `MOJOUP_NODE_SCREEN` win for the run they are given in."
    },
    "node.display.quiet": {
        "type": "boolean",
        "default": false,
        "scope": "application",
        "markdownDescription": "Headless node only: no bell or notification when something new waits for you while the terminal is not in front (the title still counts them), saved on this node (Preferences). `--quiet` and `MOJOUP_NODE_QUIET` win for the run they are given in."
    },
    "node.display.renderer": {
        "type": "string",
        "default": "classic",
        "enum": [
            "classic",
            "ink"
        ],
        "enumDescriptions": [
            "The standard dashboard.",
            "The dashboard as framed panels, drawn with Ink (on trial)."
        ],
        "scope": "application",
        "markdownDescription": "Headless node only: which renderer draws `mojoup-node`'s dashboard, saved on this node (Preferences). `ink` is the framed-panel dashboard on trial (D285). `--renderer` and `MOJOUP_NODE_RENDERER` win for the run they are given in."
    },
    "node.display.mouse": {
        "type": "string",
        "default": "auto",
        "enum": [
            "auto",
            "on",
            "off"
        ],
        "enumDescriptions": [
            "The framed-panel (Ink) dashboard takes the mouse at a desk, not over SSH.",
            "The framed-panel dashboard always takes the mouse, over SSH too.",
            "Never: keys only, and the terminal's own selection works without a modifier."
        ],
        "scope": "application",
        "markdownDescription": "Headless node only: whether `mojoup-node`'s framed-panel (Ink) dashboard takes the mouse — click a request, a host, a choice or a palette row, scroll with the wheel, drag to select log lines — saved on this node (Preferences). While it does, Shift-drag (Option-drag in macOS Terminal and iTerm2) selects text the terminal's own way. The standard dashboard and the REPL never take it. `--mouse`, `--no-mouse` and `MOJOUP_NODE_MOUSE` win for the run they are given in."
    },
    "node.consent.modelDownload": {
        "type": "boolean",
        "default": false,
        "scope": "application",
        "description": "Headless node only: the answer to \"download the llama.cpp build and this model?\", which a desktop asks in a dialog. Off declines, so a node never downloads gigabytes nobody agreed to."
    },
    "node.consent.organisationPolicy": {
        "type": "string",
        "enum": [
            "replace",
            "keep"
        ],
        "default": "replace",
        "scope": "application",
        "description": "Headless node only: when the organisation publishes a policy for a repository that already has its own .mojoup/policy.json, replace it with the organisation's (the default on a managed node) or keep the repository's."
    },
    "node.repositorySetup": {
        "type": "string",
        "enum": [
            "required",
            "off"
        ],
        "enumDescriptions": [
            "Write what the organisation requires into each repository the node serves.",
            "Never write the set-up into a repository."
        ],
        "default": "required",
        "scope": "application",
        "description": "Headless node only: \"Set up for agents\" with nobody to tick the list. Required writes only what the organisation requires (its installed hooks) into each repository the node serves, when its workbench starts and every hour, keeping the repository's own text; off writes nothing. A person sets up the rest from the workbench."
    },
    "node.sandbox.engine": {
        "type": "string",
        "enum": [
            "off",
            "docker",
            "podman"
        ],
        "default": "off",
        "scope": "application",
        "description": "Headless node only: the container engine the node offers agent sandboxes on (role sandbox). Off offers none."
    },
    "node.sandbox.images": {
        "type": "array",
        "items": {
            "type": "string"
        },
        "default": [],
        "scope": "application",
        "description": "Headless node only: the sandbox images the node offers (at most 32)."
    },
    "node.sandbox.maxConcurrent": {
        "type": "number",
        "default": 0,
        "minimum": 0,
        "scope": "application",
        "description": "Headless node only: the most sandboxes at once (0 = not stated)."
    },
    "node.sandbox.egress": {
        "type": "string",
        "enum": [
            "policy",
            "none"
        ],
        "default": "policy",
        "scope": "application",
        "description": "Headless node only: sandbox network access — through the node's policy, or none at all."
    },
    "node.computer.kind": {
        "type": "string",
        "enum": [
            "off",
            "browser",
            "desktop"
        ],
        "default": "off",
        "scope": "application",
        "description": "Headless node only: the remote computer use the node offers (role computer): a browser, a whole desktop, or none."
    },
    "node.computer.display": {
        "type": "string",
        "enum": [
            "headless",
            "xvfb",
            "vnc",
            "native"
        ],
        "default": "headless",
        "scope": "application",
        "description": "Headless node only: how that computer is drawn."
    },
    "node.computer.sessions": {
        "type": "number",
        "default": 0,
        "minimum": 0,
        "scope": "application",
        "description": "Headless node only: the most computer-use sessions at once (0 = not stated)."
    },
    "officeLayout": {
        "type": "object",
        "default": {},
        "scope": "window",
        "description": "The solo office layout (furniture, walls, floors) for ad-hoc agents. Managed by the workbench; team layouts live in .mojoup/teams/<team>.json."
    },
    "llm.currency": {
        "type": "string",
        "default": "",
        "description": "ISO 4217 code used to display model spend (for example AUD). Leave empty to follow your computer's locale."
    },
    "llm.currencyRate": {
        "type": "number",
        "default": 0,
        "minimum": 0,
        "description": "Pin the USD to display-currency rate. Zero fetches the rate automatically."
    },
    "llm.currencyRates.autoRefresh": {
        "type": "boolean",
        "default": true,
        "description": "Refresh exchange rates automatically from the rates URL."
    },
    "llm.currencyRates.url": {
        "type": "string",
        "default": "https://api.frankfurter.dev/v1/latest?base=USD",
        "description": "Exchange-rate source (Frankfurter-compatible JSON)."
    }
};
//# sourceMappingURL=declarations.js.map